import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../ui/use-toast';
import { EmulatorSaveDialog } from './EmulatorSaveDialog';
import { RetroPlayGamepadDialog } from './RetroPlayGamepadDialog';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useEmulationAnalytics } from '../../hooks/useEmulationAnalytics';
import { useAuth } from '../../hooks/useAuth';
import { useCloudSaves, type CloudSaveListItem } from '../../hooks/useCloudSaves';
import type { EmulatorSaveType } from '../../hooks/useSaveQueue';
import useOsDark from '../../hooks/useOsDark';
import { supabase } from '../../integrations/supabase/client';
import { cn } from '../../lib/utils';
import { getAssetPath } from '../../utils/assetPath';
import {
  Clock3,
  Cloud,
  Cpu,
  FolderOpen,
  HardDriveDownload,
  Library,
  Loader2,
  Maximize2,
  Minimize2,
  Monitor,
  Play,
  Sparkles,
  Square,
  Trophy,
  Upload,
  X,
  Zap,
  Gamepad2,
} from 'lucide-react';

declare global {
  interface Window {
    Dos: any;
    EJS_player: string;
    EJS_gameUrl: string;
    EJS_core: string;
    EJS_gameName: string;
    EJS_startOnLoaded: boolean;
    EJS_fullscreenOnLoaded: boolean;
    EJS_saveStateSupported: boolean;
    EJS_networkSupported: boolean;
    EJS_defaultControls?: Record<number, Record<number, { value: string; value2?: string }>>;
    EJS_VirtualGamepadSettings?: any;
    EJS_cheats?: boolean;
    EJS_loadDisplaySettings?: () => void;
    __UNIVERSAL_EMULATOR_CAPTURE_THUMBNAIL?: () => Promise<string | null>;
    __UNIVERSAL_EMULATOR_GET_STATE?: () => Promise<Uint8Array | ArrayBuffer | string | null>;
    __UNIVERSAL_EMULATOR_LOAD_STATE?: (state: Uint8Array | ArrayBuffer | string) => Promise<boolean>;
  }
}

interface UniversalEmulatorProps {
  onClose?: () => void;
}

type EmulatorRuntime = 'dos' | 'console';
type EmulatorCategory = 'console' | 'handheld' | 'computer' | 'arcade';
type MainPanelTab = 'game' | 'library';

type CaptureSourceElement = HTMLCanvasElement | HTMLVideoElement | HTMLImageElement;

interface EmulatorSystem {
  id: string;
  name: string;
  shortName: string;
  icon: string;
  runtime: EmulatorRuntime;
  core: string;
  extensions: string[];
  description: string;
  category: EmulatorCategory;
  year: number;
  popular: boolean;
  imageAsset?: string;
  heroBackgroundAsset?: string;
  heroGradient: string;
  heroAccent: string;
  headline: string;
  nostalgia: string;
}

interface GameItem {
  name: string;
  url: string;
  description: string;
  system: string;
}

interface RomLibraryItem {
  id: string;
  user_id: string;
  system_id: string;
  title: string;
  original_file_name: string;
  storage_path: string;
  thumbnail_path: string | null;
  file_size: number;
  mime_type: string | null;
  play_count: number;
  last_played_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ConnectedGamepadInfo {
  id: string;
  index: number;
  mapping: string;
}

const ROM_BUCKET = 'emulator-roms';
const QUICK_ACCESS_LIMIT = 4;
const THUMBNAIL_SIGNED_URL_TTL_SECONDS = 60 * 60;
const THUMBNAIL_CAPTURE_DELAY_MS = 2500;
const THUMBNAIL_CAPTURE_MAX_ATTEMPTS = 4;
const THUMBNAIL_CAPTURE_RETRY_DELAY_MS = 1500;
const AUTO_SAVE_INTERVAL_OPTIONS = [0, 2, 5] as const;
const DEFAULT_PLAYER_ONE_CONTROLS: Record<number, { value?: string; value2?: string }> = {

  0: { value: 'x', value2: 'BUTTON_2' },
  1: { value: 's', value2: 'BUTTON_4' },
  2: { value: 'v', value2: 'SELECT' },
  3: { value: 'enter', value2: 'START' },
  4: { value: 'up arrow', value2: 'DPAD_UP' },
  5: { value: 'down arrow', value2: 'DPAD_DOWN' },
  6: { value: 'left arrow', value2: 'DPAD_LEFT' },
  7: { value: 'right arrow', value2: 'DPAD_RIGHT' },
  8: { value: 'z', value2: 'BUTTON_1' },
  9: { value: 'a', value2: 'BUTTON_3' },
  10: { value: 'q', value2: 'LEFT_TOP_SHOULDER' },
  11: { value: 'e', value2: 'RIGHT_TOP_SHOULDER' },
  12: { value: 'tab', value2: 'LEFT_BOTTOM_SHOULDER' },
  13: { value: 'r', value2: 'RIGHT_BOTTOM_SHOULDER' },
  14: { value: '', value2: 'LEFT_STICK' },
  15: { value: '', value2: 'RIGHT_STICK' },
  16: { value: 'h', value2: 'LEFT_STICK_X:+1' },
  17: { value: 'f', value2: 'LEFT_STICK_X:-1' },
  18: { value: 'g', value2: 'LEFT_STICK_Y:+1' },
  19: { value: 't', value2: 'LEFT_STICK_Y:-1' },
  20: { value: 'l', value2: 'RIGHT_STICK_X:+1' },
  21: { value: 'j', value2: 'RIGHT_STICK_X:-1' },
  22: { value: 'k', value2: 'RIGHT_STICK_Y:+1' },
  23: { value: 'i', value2: 'RIGHT_STICK_Y:-1' },
  24: { value: '1' },
  25: { value: '2' },
  26: { value: '3' },
  27: {},
  28: {},
  29: {},
};

const buildDefaultControllerMap = () => ({
  0: Object.fromEntries(
    Object.entries(DEFAULT_PLAYER_ONE_CONTROLS).map(([key, control]) => [Number(key), { ...control }])
  ) as Record<number, { value: string; value2?: string }>,
  1: {},
  2: {},
  3: {},
});

const SYSTEMS: EmulatorSystem[] = [
  {
    id: 'dos',
    name: 'DOS Computer',
    shortName: 'DOS',
    icon: '💻',
    runtime: 'dos',
    core: 'js-dos',
    extensions: ['.jsdos', '.zip'],
    description: 'Classic PC gaming from the shareware and floppy-disk era.',
    category: 'computer',
    year: 1981,
    popular: true,
    imageAsset: 'dos-emulator',
    heroBackgroundAsset: '/os/assets/hero/dos-lan-party.jpg',
    heroGradient: 'from-amber-950 via-orange-950 to-stone-950',
    heroAccent: 'text-amber-300',
    headline: 'Boot back into the golden age of PC gaming.',
    nostalgia: 'DOS boots, CRT glow, keyboard clicks, and the magic of typing your way into a game.',
  },
  {
    id: 'nes',
    name: 'Nintendo Entertainment System',
    shortName: 'NES',
    icon: '🎮',
    runtime: 'console',
    core: 'nestopia',
    extensions: ['.nes', '.zip'],
    description: 'Classic 8-bit Nintendo console.',
    category: 'console',
    year: 1985,
    popular: true,
    imageAsset: 'nes',
    heroBackgroundAsset: '/os/assets/hero/nes-box-art.avif',
    heroGradient: 'from-red-950 via-zinc-950 to-neutral-950',
    heroAccent: 'text-red-300',
    headline: 'The 8-bit icon that defined a generation.',
    nostalgia: 'Cartridges, clicky controllers, and unforgettable living-room adventures after school.',
  },
  {
    id: 'snes',
    name: 'Super Nintendo',
    shortName: 'SNES',
    icon: '🕹️',
    runtime: 'console',
    core: 'snes9x',
    extensions: ['.smc', '.sfc', '.zip'],
    description: '16-bit Nintendo powerhouse.',
    category: 'console',
    year: 1990,
    popular: true,
    imageAsset: 'snes',
    heroBackgroundAsset: '/os/assets/hero/snes-retro.webp',
    heroGradient: 'from-violet-950 via-fuchsia-950 to-zinc-950',
    heroAccent: 'text-fuchsia-200',
    headline: 'Where 16-bit worlds felt impossibly huge.',
    nostalgia: 'Layered music, vibrant sprites, and couch co-op afternoons that never felt long enough.',
  },
  {
    id: 'genesis',
    name: 'Sega Genesis / Mega Drive',
    shortName: 'Sega',
    icon: '⚡',
    runtime: 'console',
    core: 'genesis_plus_gx',
    extensions: ['.md', '.gen', '.zip'],
    description: 'Sega\'s 16-bit blast-processing era.',
    category: 'console',
    year: 1988,
    popular: true,
    imageAsset: 'sega',
    heroBackgroundAsset: '/os/assets/hero/sega-genesis.png',
    heroGradient: 'from-blue-950 via-cyan-950 to-slate-950',
    heroAccent: 'text-cyan-200',
    headline: 'Fast, loud, and unapologetically arcade.',
    nostalgia: 'Speed, attitude, neon logos, and that unmistakable 16-bit Sega swagger.',
  },
  {
    id: 'gba',
    name: 'Game Boy Advance',
    shortName: 'GBA',
    icon: '📱',
    runtime: 'console',
    core: 'mgba',
    extensions: ['.gba', '.zip'],
    description: '32-bit handheld gaming on the go.',
    category: 'handheld',
    year: 2001,
    popular: true,
    imageAsset: 'gameboyadvanced',
    heroBackgroundAsset: '/os/assets/hero/gba.jpg',
    heroGradient: 'from-indigo-950 via-violet-950 to-slate-950',
    heroAccent: 'text-indigo-200',
    headline: 'Pocket-sized adventures with big-console energy.',
    nostalgia: 'Road trips, link cables, and late-night sessions under a bedside lamp.',
  },
  {
    id: 'n64',
    name: 'Nintendo 64',
    shortName: 'N64',
    icon: '🎯',
    runtime: 'console',
    core: 'mupen64plus_next',
    extensions: ['.n64', '.z64', '.zip'],
    description: 'Nintendo\'s 3D multiplayer revolution.',
    category: 'console',
    year: 1996,
    popular: true,
    imageAsset: 'n64',
    heroBackgroundAsset: '/os/assets/hero/n64-orange.jpeg',
    heroGradient: 'from-emerald-950 via-lime-950 to-zinc-950',
    heroAccent: 'text-lime-200',
    headline: 'The era of four controllers and endless rematches.',
    nostalgia: 'Split-screen rivalries, chunky polygons, and unforgettable weekends with friends.',
  },
  {
    id: 'psx',
    name: 'Sony PlayStation',
    shortName: 'PSX',
    icon: '💿',
    runtime: 'console',
    core: 'pcsx_rearmed',
    extensions: ['.cue', '.bin', '.zip', '.7z'],
    description: 'CD-based 32-bit console gaming.',
    category: 'console',
    year: 1994,
    popular: true,
    imageAsset: 'psx',
    heroBackgroundAsset: '/os/assets/hero/psx.webp',
    heroGradient: 'from-slate-900 via-zinc-900 to-neutral-950',
    heroAccent: 'text-slate-200',
    headline: 'A cinematic leap into the CD era.',
    nostalgia: 'Memory cards, jewel cases, startup chimes, and discovering entirely new worlds.',
  },
  {
    id: 'gb',
    name: 'Game Boy',
    shortName: 'GB',
    icon: '🟢',
    runtime: 'console',
    core: 'gambatte',
    extensions: ['.gb', '.zip'],
    description: 'Portable monochrome gaming.',
    category: 'handheld',
    year: 1989,
    popular: false,
    imageAsset: 'gameboy',
    heroBackgroundAsset: '/os/assets/hero/gameboy.webp',
    heroGradient: 'from-stone-900 via-zinc-900 to-lime-950',
    heroAccent: 'text-lime-200',
    headline: 'Portable gaming that felt futuristic.',
    nostalgia: 'That green screen, the chunky shell, and games you could carry everywhere.',
  },
  {
    id: 'gbc',
    name: 'Game Boy Color',
    shortName: 'GBC',
    icon: '🌈',
    runtime: 'console',
    core: 'gambatte',
    extensions: ['.gbc', '.zip'],
    description: 'Color handheld gaming.',
    category: 'handheld',
    year: 1998,
    popular: false,
    imageAsset: 'gameboycolor',
    heroBackgroundAsset: '/os/assets/hero/gameboy-color.jpg',
    heroGradient: 'from-teal-950 via-sky-950 to-violet-950',
    heroAccent: 'text-sky-200',
    headline: 'Pocket adventures in color.',
    nostalgia: 'Translucent shells, brighter screens, and a handheld that felt instantly modern.',
  },
  {
    id: 'arcade',
    name: 'Arcade',
    shortName: 'Arcade',
    icon: '🕹️',
    runtime: 'console',
    core: 'mame',
    extensions: ['.zip'],
    description: 'Classic arcade cabinets and coin-op chaos.',
    category: 'arcade',
    year: 1980,
    popular: false,
    heroBackgroundAsset: '/os/assets/hero/arcade.jpg',
    heroGradient: 'from-orange-950 via-rose-950 to-zinc-950',
    heroAccent: 'text-orange-200',
    headline: 'Bring the arcade floor home.',
    nostalgia: 'Joysticks, glowing marquees, and the pressure of making every credit count.',
  },
  {
    id: 'atari2600',
    name: 'Atari 2600',
    shortName: 'Atari',
    icon: '🏁',
    runtime: 'console',
    core: 'stella',
    extensions: ['.a26', '.zip'],
    description: 'The pioneer of home console gaming.',
    category: 'console',
    year: 1977,
    popular: false,
    imageAsset: 'atari',
    heroBackgroundAsset: '/os/assets/hero/atari.jpg',
    heroGradient: 'from-amber-950 via-orange-950 to-neutral-950',
    heroAccent: 'text-amber-200',
    headline: 'The roots of the home-console era.',
    nostalgia: 'Woodgrain styling, simple controls, and the earliest sparks of console magic.',
  },
];

const DEMO_GAMES: GameItem[] = [
  {
    name: 'Digger',
    url: 'https://v8.js-dos.com/bundles/digger.jsdos',
    description: 'Classic DOS arcade game',
    system: 'dos',
  },
  {
    name: 'Flappy Bird',
    url: 'https://raw.githubusercontent.com/EmulatorJS/EmulatorJS/main/examples/roms/Flappy%20Bird.gb',
    description: 'Popular mobile-style game on Game Boy',
    system: 'gb',
  },
];

const ALL_SUPPORTED_EXTENSIONS = Array.from(
  new Set(SYSTEMS.flatMap((system) => system.extensions))
).join(',');

const stripExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');
const formatBytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;
const sanitizeStorageName = (fileName: string) => fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const formatLibraryDate = (value: string | null) => {
  if (!value) return 'Never played';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
};

const compareRoms = (a: RomLibraryItem, b: RomLibraryItem) => {
  if (b.play_count !== a.play_count) {
    return b.play_count - a.play_count;
  }

  const aLast = a.last_played_at ? new Date(a.last_played_at).getTime() : 0;
  const bLast = b.last_played_at ? new Date(b.last_played_at).getTime() : 0;
  if (bLast !== aLast) {
    return bLast - aLast;
  }

  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
};

const resolveSystemForFile = (fileName: string, preferredSystem: EmulatorSystem) => {
  const normalized = fileName.toLowerCase();
  const extension = normalized.includes('.') ? normalized.slice(normalized.lastIndexOf('.')) : '';

  if (extension === '.zip') {
    return preferredSystem;
  }

  return preferredSystem.extensions.includes(extension)
    ? preferredSystem
    : SYSTEMS.find((system) => system.id !== 'dos' && system.extensions.includes(extension))
      ?? SYSTEMS.find((system) => system.extensions.includes(extension));
};

const SystemIconVisual: React.FC<{
  system: EmulatorSystem;
  className?: string;
  fallbackClassName?: string;
}> = ({ system, className = '', fallbackClassName = '' }) => {
  const src = system.imageAsset ? getAssetPath(`/os/assets/icons/${system.imageAsset}.svg`) : null;
  const [hasAssetError, setHasAssetError] = useState(false);

  useEffect(() => {
    setHasAssetError(false);
  }, [src]);

  if (src && !hasAssetError) {
    return (
      <img
        src={src}
        alt={system.name}
        className={className}
        draggable={false}
        onError={() => setHasAssetError(true)}
      />
    );
  }

  return <span className={fallbackClassName}>{system.icon}</span>;
};

const RomThumbnailVisual: React.FC<{
  rom: RomLibraryItem;
  system: EmulatorSystem;
  thumbnailUrl?: string;
  className?: string;
}> = ({ rom, system, thumbnailUrl, className = '' }) => {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/10 bg-black/20 ${className}`}>
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt={`${rom.title} thumbnail`} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-white/10 via-white/5 to-transparent">
          <SystemIconVisual
            system={system}
            className="h-12 w-12 object-contain opacity-95"
            fallbackClassName="text-4xl leading-none"
          />
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
    </div>
  );
};

export const UniversalEmulator: React.FC<UniversalEmulatorProps> = () => {
  const dosRef = useRef<HTMLDivElement>(null);
  const dosElementRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ciRef = useRef<any>(null);
  const runningViewportRef = useRef<HTMLDivElement>(null);
  const consoleFrameRef = useRef<HTMLIFrameElement>(null);
  const activeObjectUrlRef = useRef<string | null>(null);
  const activeRestoreObjectUrlRef = useRef<string | null>(null);
  const currentPlayingRomIdRef = useRef<string | null>(null);
  const pendingConsoleAutoResumeRef = useRef<CloudSaveListItem | null>(null);
  const autoSaveInFlightRef = useRef(false);
  const thumbnailCaptureTimeoutRef = useRef<number | null>(null);

  const [currentEmulator, setCurrentEmulator] = useState<'none' | 'dos' | 'console'>('none');

  const [selectedSystemId, setSelectedSystemId] = useState('nes');
  const [isRunning, setIsRunning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isJsDosLoaded, setIsJsDosLoaded] = useState(false);
  const [isEmulatorJSLoaded, setIsEmulatorJSLoaded] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [currentGameTitle, setCurrentGameTitle] = useState('');
  const [mainPanelTab, setMainPanelTab] = useState<MainPanelTab>('game');
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [isPersistingRom, setIsPersistingRom] = useState(false);
  const [activeRomId, setActiveRomId] = useState<string | null>(null);
  const [activePlayingRomId, setActivePlayingRomId] = useState<string | null>(null);
  const [deletingRomId, setDeletingRomId] = useState<string | null>(null);
  const [isSavingState, setIsSavingState] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogLoading, setSaveDialogLoading] = useState(false);
  const [saveDialogSaves, setSaveDialogSaves] = useState<CloudSaveListItem[]>([]);
  const [loadingSaveId, setLoadingSaveId] = useState<string | null>(null);
  const [deletingSaveId, setDeletingSaveId] = useState<string | null>(null);
  const [connectedGamepads, setConnectedGamepads] = useState<ConnectedGamepadInfo[]>([]);
  const [isGamepadTesterOpen, setIsGamepadTesterOpen] = useState(false);

  const [autoSaveStatus, setAutoSaveStatus] = useState<{
    tone: 'idle' | 'success' | 'error';
    message: string;
  }>({
    tone: 'idle',
    message: '',
  });
  const [romLibrary, setRomLibrary] = useState<RomLibraryItem[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [consoleSession, setConsoleSession] = useState<{
    url: string;
    system: EmulatorSystem;
    gameName: string;
    sourceFileName?: string;
  } | null>(null);

  const { user } = useAuth();
  const { toast } = useToast();
  const isDark = useOsDark();
  const supabaseAny = supabase as any;

  const { settings: globalAnalytics } = useUserSettings('emulation-analytics', { enabled: false });
  const { settings: appAnalytics, updateSetting: updateAppAnalytics } = useUserSettings('universal-emulator', {
    analyticsEnabled: false,
    screenshotsEnabled: false,
    cloudSavesEnabled: true,
    autoResumeEnabled: true,
    autoSaveIntervalMinutes: 5,
  });
  const analyticsEnabled = Boolean(globalAnalytics.enabled) && Boolean(appAnalytics.analyticsEnabled);
  const cloudSavesEnabled = Boolean(appAnalytics.cloudSavesEnabled);
  const autoResumeEnabled = Boolean(appAnalytics.autoResumeEnabled);
  const autoSaveIntervalMinutes = AUTO_SAVE_INTERVAL_OPTIONS.includes(appAnalytics.autoSaveIntervalMinutes as 0 | 2 | 5)
    ? appAnalytics.autoSaveIntervalMinutes
    : 5;
  const selectedControllerId = connectedGamepads[0]?.id || '';
  const { listSaves, saveState, loadStateBlob, getLatestAutosave, deleteState, retryPendingSaves, isSyncingPendingSaves } = useCloudSaves();

  const { logCustomEvent, captureScreenshot } = useEmulationAnalytics({
    appName: 'UniversalEmulator',
    appPath: currentPath,
    enabled: analyticsEnabled,
    screenshotsEnabled: Boolean(appAnalytics.screenshotsEnabled),
    flushIntervalMs: 2000,
    mouseThrottleMs: 400,
  });

  const selectedSystem = useMemo(
    () => SYSTEMS.find((system) => system.id === selectedSystemId) ?? SYSTEMS[0],
    [selectedSystemId]
  );
  const selectedSystemDemo = useMemo(
    () => DEMO_GAMES.find((game) => game.system === selectedSystem.id) ?? null,
    [selectedSystem.id]
  );
  const activeRunningSystem = currentEmulator === 'console' && consoleSession ? consoleSession.system : selectedSystem;
  const headerTitle = isRunning && currentGameTitle ? currentGameTitle : 'Retro Play';

  const headerSubtitle = isRunning
    ? `${activeRunningSystem.name} running`
    : 'Choose a system, upload a ROM, and enjoy.';
  const totalLibraryCount = romLibrary.length;
  const shellClass = isDark
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white'
    : 'bg-gradient-to-br from-slate-100 via-white to-slate-50 text-slate-950';
  const chromeClass = isDark ? 'border-white/10 bg-black/25' : 'border-slate-200/80 bg-white/80';
  const railClass = isDark ? 'border-white/10 bg-black/20 text-white/90' : 'border-slate-200/80 bg-white/70 text-slate-800';
  const sidebarClass = isDark ? 'border-white/10 bg-black/10' : 'border-slate-200/80 bg-white/55';
  const cardClass = isDark ? 'border-white/10 shadow-sm' : 'border-slate-200/80 bg-white/82 shadow-sm';
  const insetClass = isDark ? 'border-white/10 bg-black/20' : 'border-slate-200/80 bg-white/80';
  const softInsetClass = isDark ? 'border-white/10 bg-black/15 text-white/70' : 'border-slate-200/80 bg-white/65 text-slate-700';
  const badgeClass = isDark ? 'bg-white/10 text-white hover:bg-white/10' : 'bg-slate-900 text-white hover:bg-slate-900/90';
  const mutedTextClass = isDark ? 'text-white/60' : 'text-slate-600';
  const softTextClass = isDark ? 'text-white/70' : 'text-slate-700';
  const faintTextClass = isDark ? 'text-white/55' : 'text-slate-500';
  const mainPanelClass = isDark
    ? 'bg-slate-950 text-white'
    : 'bg-gradient-to-br from-white via-slate-50 to-slate-100 text-slate-950';
  const tabListClass = isDark ? 'grid w-full max-w-[320px] grid-cols-2 bg-white/10' : 'grid w-full max-w-[320px] grid-cols-2 bg-slate-200/80';
  const tabTriggerClass = isDark
    ? 'gap-2 data-[state=active]:bg-white data-[state=active]:text-slate-950'
    : 'gap-2 text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-950';
  const heroGlassClass = isDark ? 'border-white/15 bg-white/10' : 'border-white/70 bg-white/65 shadow-xl';
  const libraryPanelClass = isDark
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-black'
    : 'bg-gradient-to-br from-slate-100 via-white to-slate-50';
  const libraryCardClass = isDark
    ? 'border-white/10 bg-white/6 transition-all hover:border-white/20 hover:bg-white/10'
    : 'border-slate-200/80 bg-white/80 transition-all hover:border-slate-300 hover:bg-white shadow-sm';
  const quickAccessCardClass = isDark
    ? 'group overflow-hidden rounded-2xl border border-white/10 bg-black/20 text-left transition-all hover:bg-black/30 disabled:opacity-70'
    : 'group overflow-hidden rounded-2xl border border-slate-200/80 bg-white/78 text-left transition-all hover:bg-white disabled:opacity-70 shadow-sm';
  const dismissButtonClass = isDark
    ? 'absolute right-3 top-3 z-10 h-7 w-7 rounded-full bg-black/20 text-white/55 opacity-80 hover:bg-black/35 hover:text-white disabled:opacity-40'
    : 'absolute right-3 top-3 z-10 h-7 w-7 rounded-full border border-slate-200/80 bg-white/90 text-slate-500 opacity-90 shadow-sm hover:bg-white hover:text-slate-900 disabled:opacity-40';

  const selectedSystemLibrary = useMemo(
    () => romLibrary.filter((rom) => rom.system_id === selectedSystem.id).sort(compareRoms),
    [romLibrary, selectedSystem.id]
  );
  const quickAccessRoms = useMemo(
    () => selectedSystemLibrary.slice(0, QUICK_ACCESS_LIMIT),
    [selectedSystemLibrary]
  );
  const libraryRoms = useMemo(
    () => [...romLibrary].sort(compareRoms),
    [romLibrary]
  );
  const activePlayingRom = useMemo(
    () => romLibrary.find((rom) => rom.id === activePlayingRomId) ?? null,
    [activePlayingRomId, romLibrary]
  );
  const consoleHtml = useMemo(() => {

    if (!consoleSession) {
      return '';
    }

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta http-equiv="Content-Security-Policy" content="default-src 'self' blob: data: https:; connect-src 'self' blob: data: https:; img-src 'self' blob: data: https:; media-src 'self' blob: data: https:; script-src 'self' blob: 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; font-src 'self' data:; worker-src 'self' blob:; child-src 'self' blob:; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'" />
    <style>
      html, body {
        margin: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: #000;
      }

      body,
      #game {
        outline: none;
      }

      #game {
        width: 100%;
        height: 100%;
      }
    </style>
  </head>
  <body tabindex="0">
    <div id="game" tabindex="0"></div>

    <script>
      window.EJS_defaultControls = ${JSON.stringify(buildDefaultControllerMap())};

      if (!window.__UNIVERSAL_EMULATOR_WEBGL_CAPTURE_PATCHED) {
        window.__UNIVERSAL_EMULATOR_WEBGL_CAPTURE_PATCHED = true;

        window.Module = window.Module || {};
        window.Module.webglContextAttributes = {
          ...(window.Module.webglContextAttributes || {}),
          preserveDrawingBuffer: true,
        };

        const withPreservedDrawingBuffer = (originalGetContext) => function(type, attributes) {
          if (typeof type === 'string' && /^(webgl|webgl2|experimental-webgl)$/i.test(type)) {
            return originalGetContext.call(this, type, {
              ...(attributes || {}),
              preserveDrawingBuffer: true,
            });
          }

          return originalGetContext.call(this, type, attributes);
        };

        if (window.HTMLCanvasElement && HTMLCanvasElement.prototype.getContext) {
          HTMLCanvasElement.prototype.getContext = withPreservedDrawingBuffer(HTMLCanvasElement.prototype.getContext);
        }

        if (window.OffscreenCanvas && OffscreenCanvas.prototype.getContext) {
          OffscreenCanvas.prototype.getContext = withPreservedDrawingBuffer(OffscreenCanvas.prototype.getContext);
        }

      }

      window.__UNIVERSAL_EMULATOR_CAPTURE_THUMBNAIL = async () => {
        console.log('[UniversalEmulator][capture][iframe] begin', {
          core: window.EJS_core,
          hasEmulator: Boolean(window.EJS_emulator),
          hasCanvas: Boolean(window.EJS_emulator && window.EJS_emulator.canvas),
          hasGameManager: Boolean(window.EJS_emulator && window.EJS_emulator.gameManager),
        });

        const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
          reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
          reader.readAsDataURL(blob);
        });

        const sourceToDataUrl = (source) => {
          const sourceWidth = source instanceof HTMLCanvasElement
            ? source.width
            : source instanceof HTMLVideoElement
              ? source.videoWidth
              : source.naturalWidth;

          const sourceHeight = source instanceof HTMLCanvasElement
            ? source.height
            : source instanceof HTMLVideoElement
              ? source.videoHeight
              : source.naturalHeight;

          if (!sourceWidth || !sourceHeight) {
            console.log('[UniversalEmulator][capture][iframe] source rejected: missing dimensions');
            return null;
          }

          if (source instanceof HTMLCanvasElement) {
            try {
              const directDataUrl = source.toDataURL('image/png');
              if (directDataUrl && directDataUrl !== 'data:,') {
                console.log('[UniversalEmulator][capture][iframe] direct canvas export worked', {
                  width: sourceWidth,
                  height: sourceHeight,
                });
                return directDataUrl;
              }
            } catch (error) {
              console.log('[UniversalEmulator][capture][iframe] direct canvas export failed', {
                width: sourceWidth,
                height: sourceHeight,
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }

          const maxWidth = 480;
          const scale = Math.min(1, maxWidth / sourceWidth);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(sourceWidth * scale));
          canvas.height = Math.max(1, Math.round(sourceHeight * scale));

          const context = canvas.getContext('2d');
          if (!context) {
            console.log('[UniversalEmulator][capture][iframe] 2d context unavailable');
            return null;
          }

          context.drawImage(source, 0, 0, canvas.width, canvas.height);
          console.log('[UniversalEmulator][capture][iframe] fallback drawImage export worked', {
            sourceTag: source.tagName,
            width: sourceWidth,
            height: sourceHeight,
            outputWidth: canvas.width,
            outputHeight: canvas.height,
          });
          return canvas.toDataURL('image/jpeg', 0.82);
        };

        const pickFallbackSource = () => {
          const emulatorCanvas = window.EJS_emulator && window.EJS_emulator.canvas;
          if (emulatorCanvas instanceof HTMLCanvasElement && emulatorCanvas.width > 32 && emulatorCanvas.height > 32) {
            console.log('[UniversalEmulator][capture][iframe] using EJS canvas fallback', {
              width: emulatorCanvas.width,
              height: emulatorCanvas.height,
            });
            return emulatorCanvas;
          }

          const gameCanvas = document.querySelector('#game canvas');
          if (gameCanvas instanceof HTMLCanvasElement && gameCanvas.width > 32 && gameCanvas.height > 32) {
            console.log('[UniversalEmulator][capture][iframe] using #game canvas fallback', {
              width: gameCanvas.width,
              height: gameCanvas.height,
            });
            return gameCanvas;
          }

          const candidates = Array.from(document.querySelectorAll('canvas, video, img'));
          const eligible = candidates.filter((element) => {
            if (element instanceof HTMLCanvasElement) {
              return element.width > 32 && element.height > 32;
            }

            if (element instanceof HTMLVideoElement) {
              return element.videoWidth > 32 && element.videoHeight > 32 && element.readyState >= 2;
            }

            if (element instanceof HTMLImageElement) {
              return element.naturalWidth > 32 && element.naturalHeight > 32 && element.complete;
            }

            return false;
          });

          eligible.sort((a, b) => {
            const aWidth = a instanceof HTMLCanvasElement ? a.width : a instanceof HTMLVideoElement ? a.videoWidth : a.naturalWidth;
            const aHeight = a instanceof HTMLCanvasElement ? a.height : a instanceof HTMLVideoElement ? a.videoHeight : a.naturalHeight;
            const bWidth = b instanceof HTMLCanvasElement ? b.width : b instanceof HTMLVideoElement ? b.videoWidth : b.naturalWidth;
            const bHeight = b instanceof HTMLCanvasElement ? b.height : b instanceof HTMLVideoElement ? b.videoHeight : b.naturalHeight;
            return bWidth * bHeight - aWidth * aHeight;
          });

          const selected = eligible[0] || null;
          console.log('[UniversalEmulator][capture][iframe] generic fallback selection', {
            eligibleCount: eligible.length,
            selectedTag: selected ? selected.tagName : null,
          });
          return selected;
        };

        const gameManager = window.EJS_emulator && window.EJS_emulator.gameManager;
        if (gameManager) {
          try {
            let screenshotData = null;

            if (typeof gameManager.screenshot === 'function') {
              screenshotData = await gameManager.screenshot();
              console.log('[UniversalEmulator][capture][iframe] gameManager.screenshot() result', {
                type: screenshotData?.constructor?.name || typeof screenshotData,
                length: typeof screenshotData === 'string'
                  ? screenshotData.length
                  : screenshotData && 'byteLength' in screenshotData
                    ? screenshotData.byteLength
                    : screenshotData && 'length' in screenshotData
                      ? screenshotData.length
                      : null,
              });
            }

            if (!screenshotData && gameManager.functions && typeof gameManager.functions.screenshot === 'function') {
              gameManager.functions.screenshot();
              const FS = gameManager.FS || (gameManager.Module && gameManager.Module.FS);

              if (FS) {
                for (let attempt = 0; attempt < 20; attempt += 1) {
                  try {
                    const stat = FS.stat('/screenshot.png');
                    if (stat && stat.size > 0) {
                      screenshotData = FS.readFile('/screenshot.png');
                      console.log('[UniversalEmulator][capture][iframe] FS screenshot read', {
                        attempt,
                        size: stat.size,
                      });
                      break;
                    }
                  } catch {}

                  await new Promise((resolve) => setTimeout(resolve, 50));
                }
              }
            }

            if (screenshotData instanceof Uint8Array || screenshotData instanceof ArrayBuffer) {
              const blob = new Blob([screenshotData], { type: 'image/png' });
              const dataUrl = await blobToDataUrl(blob);
              if (dataUrl) {
                console.log('[UniversalEmulator][capture][iframe] returning screenshot blob as data URL', {
                  size: blob.size,
                  type: blob.type,
                });
                return dataUrl;
              }
            }

            if (typeof screenshotData === 'string') {
              console.log('[UniversalEmulator][capture][iframe] returning screenshot string payload');
              return screenshotData.startsWith('data:')
                ? screenshotData
                : 'data:image/png;base64,' + screenshotData;
            }
          } catch (error) {
            console.log('[UniversalEmulator][capture][iframe] gameManager screenshot path failed', {
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }

        const fallbackSource = pickFallbackSource();
        if (!fallbackSource) {
          console.log('[UniversalEmulator][capture][iframe] no fallback source found');
          return null;
        }

        try {
          return sourceToDataUrl(fallbackSource);
        } catch (error) {
          console.log('[UniversalEmulator][capture][iframe] fallback source export failed', {
            error: error instanceof Error ? error.message : String(error),
          });
          return null;
        }
      };

      window.__UNIVERSAL_EMULATOR_GET_STATE = async () => {
        const gameManager = window.EJS_emulator && window.EJS_emulator.gameManager;
        if (!gameManager || typeof gameManager.getState !== 'function') {
          return null;
        }

        const state = await gameManager.getState();
        if (!state) {
          return null;
        }

        if (state instanceof Uint8Array) {
          return state;
        }

        if (state instanceof ArrayBuffer) {
          return new Uint8Array(state);
        }

        if (typeof state === 'string') {
          const normalized = state.startsWith('data:') ? state.slice(state.indexOf(',') + 1) : state;
          const binary = atob(normalized);
          const bytes = new Uint8Array(binary.length);
          for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
          }
          return bytes;
        }

        if (state instanceof Blob) {
          return new Uint8Array(await state.arrayBuffer());
        }

        return null;
      };

      window.__UNIVERSAL_EMULATOR_LOAD_STATE = async (stateData) => {
        const gameManager = window.EJS_emulator && window.EJS_emulator.gameManager;
        if (!gameManager || typeof gameManager.loadState !== 'function') {
          return false;
        }

        let normalizedState = stateData;
        if (stateData instanceof ArrayBuffer) {
          normalizedState = new Uint8Array(stateData);
        }

        if (typeof normalizedState === 'string' && normalizedState.startsWith('data:')) {
          normalizedState = normalizedState.slice(normalizedState.indexOf(',') + 1);
        }

        await gameManager.loadState(normalizedState);
        return true;
      };

      window.EJS_onGameStart = () => {
        window.parent.postMessage({ type: 'universal-emulator-console-ready' }, '*');
      };

      window.EJS_DEBUG_XX = true;

      window.EJS_player = '#game';
      window.EJS_core = ${JSON.stringify(consoleSession.system.core)};
      window.EJS_gameUrl = ${JSON.stringify(consoleSession.url)};
      window.EJS_gameName = ${JSON.stringify(consoleSession.sourceFileName ?? consoleSession.gameName)};
      window.EJS_pathtodata = '/emulatorjs/data/';
      window.EJS_startOnLoaded = true;
      window.EJS_fullscreenOnLoaded = false;
      window.EJS_saveStateSupported = true;
      window.EJS_networkSupported = false;
      window.EJS_netplayServer = '';
      window.EJS_AdUrl = '';
      window.EJS_AdMode = false;
      window.EJS_disableAutoLang = true;
      window.EJS_language = 'en-US';
      window.EJS_cheats = true;
    </script>
    <script src="/emulatorjs/data/loader.js"></script>
  </body>
</html>`;
  }, [consoleSession]);

  const clearScheduledThumbnailCapture = () => {

    if (thumbnailCaptureTimeoutRef.current !== null) {
      window.clearTimeout(thumbnailCaptureTimeoutRef.current);
      thumbnailCaptureTimeoutRef.current = null;
    }
  };

  const releaseActiveObjectUrl = () => {
    if (activeObjectUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(activeObjectUrlRef.current);
    }
    if (activeRestoreObjectUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(activeRestoreObjectUrlRef.current);
    }
    activeObjectUrlRef.current = null;
    activeRestoreObjectUrlRef.current = null;
  };

  const mergeRomIntoLibrary = (rom: RomLibraryItem) => {
    setRomLibrary((current) => {
      const next = current.filter((item) => item.id !== rom.id);
      next.unshift(rom);
      return next;
    });
  };

  const removeRomFromLibrary = (romId: string) => {
    setRomLibrary((current) => current.filter((item) => item.id !== romId));
    setThumbnailUrls((current) => {
      const next = { ...current };
      delete next[romId];
      return next;
    });
  };

  const setThumbnailUrlForRom = async (rom: RomLibraryItem) => {
    if (!rom.thumbnail_path) {
      setThumbnailUrls((current) => {
        const next = { ...current };
        delete next[rom.id];
        return next;
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from(ROM_BUCKET)
        .createSignedUrl(rom.thumbnail_path, THUMBNAIL_SIGNED_URL_TTL_SECONDS);

      if (error || !data?.signedUrl) {
        throw error;
      }

      setThumbnailUrls((current) => ({
        ...current,
        [rom.id]: data.signedUrl,
      }));
    } catch (error) {
      console.error('Failed to create thumbnail URL:', error);
    }
  };

  const hydrateThumbnailUrls = async (roms: RomLibraryItem[]) => {
    const thumbnailRoms = roms.filter((rom) => Boolean(rom.thumbnail_path));

    if (thumbnailRoms.length === 0) {
      setThumbnailUrls({});
      return;
    }

    const nextUrls: Record<string, string> = {};

    await Promise.all(
      thumbnailRoms.map(async (rom) => {
        try {
          const { data, error } = await supabase.storage
            .from(ROM_BUCKET)
            .createSignedUrl(rom.thumbnail_path as string, THUMBNAIL_SIGNED_URL_TTL_SECONDS);

          if (!error && data?.signedUrl) {
            nextUrls[rom.id] = data.signedUrl;
          }
        } catch (error) {
          console.error('Failed to hydrate thumbnail URL:', error);
        }
      })
    );

    setThumbnailUrls(nextUrls);
  };

  const loadRomLibrary = async () => {
    if (!user) {
      setRomLibrary([]);
      setThumbnailUrls({});
      setLibraryLoading(false);
      return;
    }

    setLibraryLoading(true);
    try {
      const { data, error } = await supabaseAny
        .from('emulator_roms')
        .select('*')
        .eq('user_id', user.id)
        .order('play_count', { ascending: false })
        .order('last_played_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const roms = (data ?? []) as RomLibraryItem[];
      setRomLibrary(roms);
      await hydrateThumbnailUrls(roms);
    } catch (error: any) {
      console.error('Failed to load ROM library:', error);
      toast({
        title: 'Library unavailable',
        description: error?.message ?? 'Could not load your saved ROMs.',
        variant: 'destructive',
      });
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    setSelectedFile(null);
    setMainPanelTab('game');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedSystemId]);

  useEffect(() => {
    void loadRomLibrary();
  }, [user?.id]);

  useEffect(() => {
    return () => {
      clearScheduledThumbnailCapture();
      currentPlayingRomIdRef.current = null;
      releaseActiveObjectUrl();
      if (dosElementRef.current?.parentNode) {
        dosElementRef.current.parentNode.removeChild(dosElementRef.current);
      }
      dosElementRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === runningViewportRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleConsoleMessages = async (event: MessageEvent) => {
      if (event.source !== consoleFrameRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'universal-emulator-gamepad-debug') {
        return;
      }

      if (event.data?.type !== 'universal-emulator-console-ready') {
        return;
      }

      if (!pendingConsoleAutoResumeRef.current) {
        return;
      }

      const pendingSave = pendingConsoleAutoResumeRef.current;
      pendingConsoleAutoResumeRef.current = null;

      try {
        focusConsoleInput();
        const stateBlob = await loadStateBlob(pendingSave);
        await applyConsoleStateBlob(stateBlob);
        focusConsoleInput();
        toast({
          title: 'Auto-resume loaded',
          description:
            pendingSave.status === 'pending'
              ? 'A locally queued autosave restored your session.'
              : 'Your latest autosave was restored automatically.',
        });
      } catch (error) {
        console.error('Failed to auto-resume console save state:', error);
      }
    };

    window.addEventListener('message', handleConsoleMessages);
    return () => window.removeEventListener('message', handleConsoleMessages);
  }, [loadStateBlob, toast]);

  const ensureDosMountTarget = () => {

    if (!dosRef.current) {
      return null;
    }

    if (dosElementRef.current?.parentNode !== dosRef.current) {
      if (dosElementRef.current?.parentNode) {
        dosElementRef.current.parentNode.removeChild(dosElementRef.current);
      }

      const dosElement = document.createElement('div');
      dosElement.style.width = '100%';
      dosElement.style.height = '100%';
      dosElement.style.minHeight = '400px';
      dosElement.style.backgroundColor = '#000';
      dosRef.current.appendChild(dosElement);
      dosElementRef.current = dosElement;
    }

    return dosElementRef.current;
  };

  const focusConsoleInput = () => {
    if (currentEmulator !== 'console') {
      return;
    }

    window.setTimeout(() => {
      const iframe = consoleFrameRef.current;
      const frameWindow = iframe?.contentWindow;
      const frameDocument = frameWindow?.document;
      const gameRoot = frameDocument?.getElementById('game') as HTMLElement | null;

      iframe?.focus();
      frameWindow?.focus();
      frameDocument?.body?.focus();
      gameRoot?.focus();
    }, 0);
  };

  const setConsolePlaybackPaused = (paused: boolean) => {
    const frameWindow = consoleFrameRef.current?.contentWindow as (Window & {
      EJS_emulator?: {
        pause?: (dontUpdate?: boolean) => void;
        play?: (dontUpdate?: boolean) => void;
      };
    }) | null;

    if (!frameWindow?.EJS_emulator) {
      return;
    }

    if (paused) {
      frameWindow.EJS_emulator.pause?.(true);
      return;
    }

    frameWindow.EJS_emulator.play?.(true);
    focusConsoleInput();
  };

  const setDosPlaybackPaused = (paused: boolean) => {
    if (!ciRef.current) {
      return;
    }

    if (paused) {
      if (typeof ciRef.current.pause === 'function') {
        ciRef.current.pause();
        return;
      }
      if (typeof ciRef.current.suspend === 'function') {
        ciRef.current.suspend();
      }
      return;
    }

    if (typeof ciRef.current.resume === 'function') {
      ciRef.current.resume();
      return;
    }
    if (typeof ciRef.current.play === 'function') {
      ciRef.current.play();
    }
  };

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const shouldPause = mainPanelTab !== 'game';

    if (currentEmulator === 'console') {
      setConsolePlaybackPaused(shouldPause);
      return;
    }

    if (currentEmulator === 'dos') {
      setDosPlaybackPaused(shouldPause);
    }
  }, [consoleSession, currentEmulator, isRunning, mainPanelTab]);

  useEffect(() => {
    if (currentEmulator !== 'console' || !isRunning || mainPanelTab !== 'game') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      focusConsoleInput();
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [consoleSession, currentEmulator, isRunning, mainPanelTab]);

  useEffect(() => {

    if (
      !isRunning ||
      !activePlayingRom ||
      !user ||
      !cloudSavesEnabled ||
      autoSaveIntervalMinutes === 0 ||
      mainPanelTab !== 'game'
    ) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (autoSaveInFlightRef.current) {
        return;
      }

      autoSaveInFlightRef.current = true;
      void triggerSessionSave('auto').finally(() => {
        autoSaveInFlightRef.current = false;
      });
    }, autoSaveIntervalMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [activePlayingRom, autoSaveIntervalMinutes, cloudSavesEnabled, isRunning, mainPanelTab, user]);

  const toggleFullscreen = async () => {

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      if (runningViewportRef.current) {
        await runningViewportRef.current.requestFullscreen();
      }
    } catch (error) {
      toast({
        title: 'Fullscreen unavailable',
        description: 'The emulator view could not enter fullscreen mode.',
        variant: 'destructive',
      });
    }
  };

  const ensureDosRuntimeLoaded = async () => {
    if (!(window as Window & { __UNIVERSAL_EMULATOR_DOS_CAPTURE_PATCHED?: boolean }).__UNIVERSAL_EMULATOR_DOS_CAPTURE_PATCHED) {
      (window as Window & { __UNIVERSAL_EMULATOR_DOS_CAPTURE_PATCHED?: boolean }).__UNIVERSAL_EMULATOR_DOS_CAPTURE_PATCHED = true;

      const withPreservedDrawingBuffer = (originalGetContext: typeof HTMLCanvasElement.prototype.getContext) => {
        return function getContextWithPreservedDrawingBuffer(
          this: HTMLCanvasElement | OffscreenCanvas,
          type: string,
          attributes?: CanvasRenderingContext2DSettings | WebGLContextAttributes
        ) {
          if (typeof type === 'string' && /^(webgl|webgl2|experimental-webgl)$/i.test(type)) {
            return originalGetContext.call(this, type, {
              ...(attributes || {}),
              preserveDrawingBuffer: true,
            } as WebGLContextAttributes);
          }

          return originalGetContext.call(this, type, attributes as any);
        };
      };

      if (window.HTMLCanvasElement?.prototype.getContext) {
        HTMLCanvasElement.prototype.getContext = withPreservedDrawingBuffer(HTMLCanvasElement.prototype.getContext);
      }

      if (window.OffscreenCanvas?.prototype.getContext) {
        OffscreenCanvas.prototype.getContext = withPreservedDrawingBuffer(
          OffscreenCanvas.prototype.getContext as unknown as typeof HTMLCanvasElement.prototype.getContext
        ) as typeof OffscreenCanvas.prototype.getContext;
      }

    }

    if (window.Dos) {
      setIsJsDosLoaded(true);
      return true;
    }

    let stylesheet = document.querySelector('link[data-universal-emulator-dos-style="true"]') as HTMLLinkElement | null;
    if (!stylesheet) {
      stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = 'https://v8.js-dos.com/latest/js-dos.css';
      stylesheet.dataset.universalEmulatorDosStyle = 'true';
      document.head.appendChild(stylesheet);
    }

    const existingScript = document.querySelector('script[data-universal-emulator-dos="true"]') as HTMLScriptElement | null;

    return await new Promise<boolean>((resolve) => {
      const handleLoad = () => {
        const isReady = Boolean(window.Dos);
        setIsJsDosLoaded(isReady);
        resolve(isReady);
      };

      const handleError = () => {
        setIsJsDosLoaded(false);
        resolve(false);
      };

      const script = existingScript ?? document.createElement('script');

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existingScript) {
        script.src = 'https://v8.js-dos.com/latest/js-dos.js';
        script.crossOrigin = 'anonymous';
        script.dataset.universalEmulatorDos = 'true';
        document.head.appendChild(script);
      }
    });
  };

  useEffect(() => {
    if (selectedSystem.runtime !== 'dos') {
      return;
    }

    void ensureDosRuntimeLoaded().then((isReady) => {
      if (!isReady) {
        toast({
          title: 'DOS runtime unavailable',
          description: 'The js-dos runtime could not be loaded for DOS games.',
          variant: 'destructive',
        });
      }
    });
  }, [selectedSystem.runtime, toast]);

  useEffect(() => {
    setIsEmulatorJSLoaded(true);
  }, []);

  const pickBestCaptureSource = (root: ParentNode | null): CaptureSourceElement | null => {
    if (!root) {
      return null;
    }

    const candidates = Array.from(root.querySelectorAll('canvas, video, img')) as CaptureSourceElement[];
    const eligible = candidates.filter((element) => {
      if (element instanceof HTMLCanvasElement) {
        return element.width > 32 && element.height > 32;
      }

      if (element instanceof HTMLVideoElement) {
        return element.videoWidth > 32 && element.videoHeight > 32 && element.readyState >= 2;
      }

      if (element instanceof HTMLImageElement) {
        return element.naturalWidth > 32 && element.naturalHeight > 32 && element.complete;
      }

      return false;
    });

    eligible.sort((a, b) => {
      const aWidth = a instanceof HTMLCanvasElement ? a.width : a instanceof HTMLVideoElement ? a.videoWidth : a.naturalWidth;
      const aHeight = a instanceof HTMLCanvasElement ? a.height : a instanceof HTMLVideoElement ? a.videoHeight : a.naturalHeight;
      const bWidth = b instanceof HTMLCanvasElement ? b.width : b instanceof HTMLVideoElement ? b.videoWidth : b.naturalWidth;
      const bHeight = b instanceof HTMLCanvasElement ? b.height : b instanceof HTMLVideoElement ? b.videoHeight : b.naturalHeight;
      return bWidth * bHeight - aWidth * aHeight;
    });

    return eligible[0] ?? null;
  };

  const captureSourceToBlob = async (source: CaptureSourceElement) => {
    if (source instanceof HTMLCanvasElement) {
      try {
        const directDataUrl = source.toDataURL('image/png');
        if (directDataUrl && directDataUrl !== 'data:,') {
          return dataUrlToBlob(directDataUrl);
        }
      } catch {
      }
    }

    const sourceWidth = source instanceof HTMLCanvasElement
      ? source.width
      : source instanceof HTMLVideoElement
        ? source.videoWidth
        : source.naturalWidth;

    const sourceHeight = source instanceof HTMLCanvasElement
      ? source.height
      : source instanceof HTMLVideoElement
        ? source.videoHeight
        : source.naturalHeight;

    if (!sourceWidth || !sourceHeight) {
      return null;
    }

    const maxWidth = 480;
    const scale = Math.min(1, maxWidth / sourceWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));

    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.drawImage(source, 0, 0, canvas.width, canvas.height);

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.82);
    });
  };

  const dataUrlToBlob = async (dataUrl: string) => {
    const response = await fetch(dataUrl);
    return response.blob();
  };

  const isBlobLikelyBlankThumbnail = async (blob: Blob) => {
    if (!blob.type.startsWith('image/')) {
      return false;
    }

    try {
      const bitmap = await createImageBitmap(blob);
      const sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = 32;
      sampleCanvas.height = 32;

      const context = sampleCanvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        bitmap.close();
        return false;
      }

      context.drawImage(bitmap, 0, 0, sampleCanvas.width, sampleCanvas.height);
      bitmap.close();

      const pixels = context.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height).data;
      let visiblePixels = 0;
      let nonBlackPixels = 0;
      let totalLuminance = 0;

      for (let index = 0; index < pixels.length; index += 4) {
        const red = pixels[index];
        const green = pixels[index + 1];
        const blue = pixels[index + 2];
        const alpha = pixels[index + 3];

        if (alpha < 16) {
          continue;
        }

        visiblePixels += 1;
        const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
        totalLuminance += luminance;

        if (luminance > 12 || Math.max(red, green, blue) > 24) {
          nonBlackPixels += 1;
        }
      }

      if (visiblePixels === 0) {
        console.log('[UniversalEmulator][capture] thumbnail rejected: no visible pixels', {
          size: blob.size,
          type: blob.type,
        });
        return true;
      }

      const averageLuminance = totalLuminance / visiblePixels;
      const nonBlackRatio = nonBlackPixels / visiblePixels;
      const isBlank = averageLuminance < 10 && nonBlackRatio < 0.03;

      if (isBlank) {
        console.log('[UniversalEmulator][capture] thumbnail rejected: likely black frame', {
          size: blob.size,
          type: blob.type,
          averageLuminance,
          nonBlackRatio,
        });
      } else {
        console.log('[UniversalEmulator][capture] thumbnail accepted by pixel check', {
          size: blob.size,
          type: blob.type,
          averageLuminance,
          nonBlackRatio,
        });
      }

      return isBlank;
    } catch (error) {
      console.log('[UniversalEmulator][capture] blank-thumbnail detection failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  };

  const captureCurrentGameThumbnailBlob = async () => {
    console.log('[UniversalEmulator][capture] begin', {
      emulator: currentEmulator,
      gameTitle: currentGameTitle,
      currentPath,
    });

    const validateCapturedBlob = async (blob: Blob | null, source: string) => {
      if (!blob || blob.size === 0) {
        console.log('[UniversalEmulator][capture] blob missing or empty', { source });
        return null;
      }

      console.log('[UniversalEmulator][capture] blob candidate', {
        source,
        size: blob.size,
        type: blob.type,
      });

      if (await isBlobLikelyBlankThumbnail(blob)) {
        console.log('[UniversalEmulator][capture] blob rejected after blank check', { source });
        return null;
      }

      console.log('[UniversalEmulator][capture] blob accepted', { source });
      return blob;
    };

    if (currentEmulator === 'console') {
      const frameWindow = consoleFrameRef.current?.contentWindow as (Window & {
        __UNIVERSAL_EMULATOR_CAPTURE_THUMBNAIL?: () => Promise<string | null>;
      }) | null;

      if (frameWindow?.__UNIVERSAL_EMULATOR_CAPTURE_THUMBNAIL) {
        const dataUrl = await frameWindow.__UNIVERSAL_EMULATOR_CAPTURE_THUMBNAIL();
        if (dataUrl) {
          const capturedBlob = await validateCapturedBlob(await dataUrlToBlob(dataUrl), 'iframe-capture-helper');
          if (capturedBlob) {
            return capturedBlob;
          }
        } else {
          console.log('[UniversalEmulator][capture] iframe helper returned null');
        }
      }
    }

    if (currentEmulator === 'dos') {
      console.log('[UniversalEmulator][capture][dos] source scan', {
        hasDosRef: Boolean(dosRef.current),
        hasDosElementRef: Boolean(dosElementRef.current),
        dosRefHtmlLength: dosRef.current?.innerHTML.length ?? 0,
        dosElementHtmlLength: dosElementRef.current?.innerHTML.length ?? 0,
        dosRefCanvasCount: dosRef.current?.querySelectorAll('canvas').length ?? 0,
        dosRefVideoCount: dosRef.current?.querySelectorAll('video').length ?? 0,
        dosRefImageCount: dosRef.current?.querySelectorAll('img').length ?? 0,
        dosRefIframeCount: dosRef.current?.querySelectorAll('iframe').length ?? 0,
        dosElementCanvasCount: dosElementRef.current?.querySelectorAll('canvas').length ?? 0,
        dosElementVideoCount: dosElementRef.current?.querySelectorAll('video').length ?? 0,
        dosElementImageCount: dosElementRef.current?.querySelectorAll('img').length ?? 0,
        dosElementIframeCount: dosElementRef.current?.querySelectorAll('iframe').length ?? 0,
      });
    }

    const source = currentEmulator === 'dos'
      ? pickBestCaptureSource(dosElementRef.current ?? dosRef.current)
      : pickBestCaptureSource(consoleFrameRef.current?.contentDocument ?? null);

    if (source) {
      if (currentEmulator === 'dos') {
        console.log('[UniversalEmulator][capture][dos] selected source', {
          tagName: source.tagName,
          width: source instanceof HTMLCanvasElement
            ? source.width
            : source instanceof HTMLVideoElement
              ? source.videoWidth
              : source.naturalWidth,
          height: source instanceof HTMLCanvasElement
            ? source.height
            : source instanceof HTMLVideoElement
              ? source.videoHeight
              : source.naturalHeight,
        });
      }

      const capturedBlob = await validateCapturedBlob(
        await captureSourceToBlob(source),
        `direct-dom-${source.tagName.toLowerCase()}`
      );
      if (capturedBlob) {
        return capturedBlob;
      }
    } else {
      console.log('[UniversalEmulator][capture] no direct DOM source found');
    }

    if (currentEmulator === 'console') {
      await wait(180);

      const retrySource = pickBestCaptureSource(consoleFrameRef.current?.contentDocument ?? null);
      if (retrySource) {
        const retryBlob = await validateCapturedBlob(
          await captureSourceToBlob(retrySource),
          `retry-dom-${retrySource.tagName.toLowerCase()}`
        );
        if (retryBlob) {
          return retryBlob;
        }
      } else {
        console.log('[UniversalEmulator][capture] no retry DOM source found');
      }
    }

    console.log('[UniversalEmulator][capture] failed to capture thumbnail');
    return null;
  };

  const uploadRomThumbnail = async (rom: RomLibraryItem, thumbnailBlob: Blob) => {
    if (!user) {
      return null;
    }

    const thumbnailExtension = thumbnailBlob.type === 'image/png' ? 'png' : 'jpg';
    const thumbnailContentType = thumbnailBlob.type || (thumbnailExtension === 'png' ? 'image/png' : 'image/jpeg');
    const thumbnailPath = `${user.id}/thumbnails/${rom.id}.${thumbnailExtension}`;
    const updatedAt = new Date().toISOString();

    const { error: uploadError } = await supabase.storage
      .from(ROM_BUCKET)
      .upload(thumbnailPath, thumbnailBlob, {
        upsert: true,
        cacheControl: '60',
        contentType: thumbnailContentType,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data, error } = await supabaseAny
      .from('emulator_roms')
      .update({
        thumbnail_path: thumbnailPath,
        updated_at: updatedAt,
      })
      .eq('id', rom.id)
      .eq('user_id', user.id)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    const updatedRom = data as RomLibraryItem;
    mergeRomIntoLibrary(updatedRom);
    await setThumbnailUrlForRom(updatedRom);
    return updatedRom;
  };

  const captureAndPersistRomThumbnail = async (rom: RomLibraryItem) => {
    if (!user || currentPlayingRomIdRef.current !== rom.id) {
      return;
    }

    for (let attempt = 0; attempt < THUMBNAIL_CAPTURE_MAX_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await wait(THUMBNAIL_CAPTURE_RETRY_DELAY_MS);
      }

      if (currentPlayingRomIdRef.current !== rom.id) {
        return;
      }

      try {
        const thumbnailBlob = await captureCurrentGameThumbnailBlob();
        if (!thumbnailBlob || thumbnailBlob.size === 0) {
          continue;
        }

        await uploadRomThumbnail(rom, thumbnailBlob);
        return;
      } catch (error) {
        console.error('Failed to capture ROM thumbnail:', error);
      }
    }
  };

  const scheduleRomThumbnailCapture = (rom: RomLibraryItem) => {
    if (!user) {
      return;
    }

    clearScheduledThumbnailCapture();
    currentPlayingRomIdRef.current = rom.id;
    setActivePlayingRomId(rom.id);
    thumbnailCaptureTimeoutRef.current = window.setTimeout(() => {

      void captureAndPersistRomThumbnail(rom);
    }, THUMBNAIL_CAPTURE_DELAY_MS);
  };

  const forceCaptureActiveRomThumbnail = async () => {
    if (!user || !currentPlayingRomIdRef.current) {
      return;
    }

    const activeRom = romLibrary.find((rom) => rom.id === currentPlayingRomIdRef.current);
    if (!activeRom) {
      return;
    }

    clearScheduledThumbnailCapture();

    try {
      console.log('[UniversalEmulator][capture] forcing thumbnail capture on stop', {
        romId: activeRom.id,
        title: activeRom.title,
        emulator: currentEmulator,
      });

      const thumbnailBlob = await captureCurrentGameThumbnailBlob();
      if (!thumbnailBlob || thumbnailBlob.size === 0) {
        console.log('[UniversalEmulator][capture] force capture produced no blob', {
          romId: activeRom.id,
          emulator: currentEmulator,
        });
        return;
      }

      await uploadRomThumbnail(activeRom, thumbnailBlob);
      console.log('[UniversalEmulator][capture] force capture uploaded thumbnail', {
        romId: activeRom.id,
        emulator: currentEmulator,
      });
    } catch (error) {
      console.error('Failed to force capture ROM thumbnail:', error);
    }
  };

  const stopDosSession = async () => {
    if (!ciRef.current) {
      return;
    }

    if (typeof ciRef.current.exit === 'function') {
      await ciRef.current.exit();
    } else if (typeof ciRef.current.stop === 'function') {
      await ciRef.current.stop();
    } else if (typeof ciRef.current.destroy === 'function') {
      await ciRef.current.destroy();
    }

    ciRef.current = null;

    if (dosElementRef.current) {
      dosElementRef.current.innerHTML = '';
    }
  };

  const resolveDosBundleUrl = async (url: string) => {
    if (url.startsWith('blob:')) {
      return url;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Bundle fetch failed (${response.status})`);
    }

    const bundle = await response.arrayBuffer();
    if (bundle.byteLength === 0) {
      throw new Error('Bundle is empty or invalid');
    }

    const objectUrl = URL.createObjectURL(new Blob([bundle], { type: 'application/octet-stream' }));
    releaseActiveObjectUrl();
    activeObjectUrlRef.current = objectUrl;
    return objectUrl;
  };

  const loadDosGame = async (
    url: string,
    gameTitle = 'DOS game',
    sourceLabel = url,
    restoreBlob?: Blob | null
  ) => {
    const dosReady = await ensureDosRuntimeLoaded();

    if (!dosReady || !window.Dos) {
      toast({ title: 'Error', description: 'DOS emulator not ready', variant: 'destructive' });
      return false;
    }

    try {
      setConsoleSession(null);
      setCurrentGameTitle(gameTitle);
      setCurrentPath(sourceLabel);
      setSelectedSystemId('dos');
      setMainPanelTab('game');
      setAutoSaveStatus({ tone: 'idle', message: '' });
      logCustomEvent('start', { emulator: 'dos', path: sourceLabel, restored: Boolean(restoreBlob) });
      setCurrentEmulator('dos');
      setIsRunning(true);

      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });

      const dosMountTarget = ensureDosMountTarget();
      if (!dosMountTarget) {
        toast({ title: 'Error', description: 'DOS emulator not ready', variant: 'destructive' });
        setIsRunning(false);
        setCurrentEmulator('none');
        return false;
      }

      await stopDosSession();

      const bundleUrl = await resolveDosBundleUrl(url);
      dosMountTarget.innerHTML = '';

      const dosPlayer = window.Dos(dosMountTarget, {
        theme: isDark ? 'dark' : 'light',
      });

      if (restoreBlob) {
        activeRestoreObjectUrlRef.current = URL.createObjectURL(restoreBlob);
      }

      ciRef.current = activeRestoreObjectUrlRef.current
        ? await dosPlayer.run(bundleUrl, activeRestoreObjectUrlRef.current)
        : await dosPlayer.run(bundleUrl);

      return true;
    } catch (error) {
      console.error('Failed to load DOS game:', error);
      logCustomEvent('error', { emulator: 'dos', message: (error as any)?.message ?? 'Unknown' });
      toast({
        title: 'Error',
        description: (error as any)?.message ?? 'Failed to load DOS game',
        variant: 'destructive',
      });
      setIsRunning(false);
      setCurrentEmulator('none');
      return false;
    }
  };

  const loadConsoleGame = async (
    url: string,
    system: EmulatorSystem,
    gameName: string,
    sourceLabel = url,
    sourceFileName?: string
  ) => {
    if (!isEmulatorJSLoaded) {
      toast({ title: 'Error', description: 'Console emulator not ready', variant: 'destructive' });
      return false;
    }

    try {
      await stopDosSession();

      setCurrentGameTitle(gameName);
      setCurrentPath(sourceLabel);
      setSelectedSystemId(system.id);
      setMainPanelTab('game');
      setAutoSaveStatus({ tone: 'idle', message: '' });
      logCustomEvent('start', { emulator: 'console', system: system.id, path: sourceLabel });
      setConsoleSession({ url, system, gameName, sourceFileName });
      setCurrentEmulator('console');
      setIsRunning(true);

      return true;
    } catch (error) {
      console.error('Failed to load console game:', error);
      logCustomEvent('error', { emulator: 'console', message: (error as any)?.message ?? 'Unknown' });
      toast({ title: 'Error', description: 'Failed to load console game', variant: 'destructive' });
      setIsRunning(false);
      setCurrentEmulator('none');
      setConsoleSession(null);
      return false;
    }
  };

  const launchObjectUrl = async (
    url: string,
    system: EmulatorSystem,
    gameTitle: string,
    sourceLabel: string,
    sourceFileName?: string,
    restoreBlob?: Blob | null
  ) => {
    releaseActiveObjectUrl();
    activeObjectUrlRef.current = url;

    if (system.runtime === 'dos') {
      return loadDosGame(url, gameTitle, sourceLabel, restoreBlob);
    }

    return loadConsoleGame(url, system, gameTitle, sourceLabel, sourceFileName);
  };

  const refreshSaveDialog = async (romId = activePlayingRomId) => {
    if (!romId) {
      setSaveDialogSaves([]);
      return;
    }

    setSaveDialogLoading(true);
    try {
      const saves = await listSaves(romId);
      setSaveDialogSaves(saves);
    } catch (error: any) {
      console.error('Failed to load save states:', error);
      toast({
        title: 'Could not load save states',
        description: error?.message ?? 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaveDialogLoading(false);
    }
  };

  const prepareLaunchRestore = async (rom: RomLibraryItem, system: EmulatorSystem) => {
    pendingConsoleAutoResumeRef.current = null;

    if (!user || !cloudSavesEnabled || !autoResumeEnabled) {
      return null;
    }

    try {
      const latestAutosave = await getLatestAutosave(rom.id);
      if (!latestAutosave) {
        return null;
      }

      if (system.runtime === 'console') {
        pendingConsoleAutoResumeRef.current = latestAutosave;
        return null;
      }

      return await loadStateBlob(latestAutosave);
    } catch (error) {
      console.error('Failed to prepare auto-resume save:', error);
      pendingConsoleAutoResumeRef.current = null;
      return null;
    }
  };

  const captureConsoleStateBlob = async () => {
    const frameWindow = consoleFrameRef.current?.contentWindow;
    const statePayload = await frameWindow?.__UNIVERSAL_EMULATOR_GET_STATE?.();
    if (!statePayload) {
      return null;
    }

    if (ArrayBuffer.isView(statePayload)) {
      const view = statePayload as ArrayBufferView;
      const sourceBytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
      const bytes = new Uint8Array(sourceBytes.byteLength);
      bytes.set(sourceBytes);
      return new Blob([bytes], { type: 'application/octet-stream' });
    }

    if (Object.prototype.toString.call(statePayload) === '[object ArrayBuffer]') {
      return new Blob([statePayload], { type: 'application/octet-stream' });
    }

    if (typeof statePayload === 'string') {
      return dataUrlToBlob(`data:application/octet-stream;base64,${statePayload}`);
    }

    return null;
  };

  const applyConsoleStateBlob = async (stateBlob: Blob) => {
    const frameWindow = consoleFrameRef.current?.contentWindow;
    if (!frameWindow?.__UNIVERSAL_EMULATOR_LOAD_STATE) {
      throw new Error('Console save-state bridge is not ready yet.');
    }

    const stateBuffer = await stateBlob.arrayBuffer();
    const didLoad = await frameWindow.__UNIVERSAL_EMULATOR_LOAD_STATE(new Uint8Array(stateBuffer));
    if (!didLoad) {
      throw new Error('The console save state could not be loaded.');
    }
  };

  const captureDosStateBlob = async () => {
    if (!ciRef.current?.persist) {
      return null;
    }

    const persisted = await ciRef.current.persist();
    if (!persisted) {
      return null;
    }

    const bytes = persisted instanceof Uint8Array ? persisted : new Uint8Array(persisted);
    if (bytes.byteLength === 0) {
      return null;
    }

    return new Blob([bytes], { type: 'application/octet-stream' });
  };

  const openSaveDialogForCurrentRom = async () => {
    if (!activePlayingRom) {
      return;
    }

    setSaveDialogOpen(true);
    await refreshSaveDialog(activePlayingRom.id);
  };

  const createSessionSave = async (type: EmulatorSaveType, screenshotBlob?: Blob | null) => {
    if (!user) {
      throw new Error('Sign in to enable cloud saves.');
    }

    if (!cloudSavesEnabled) {
      throw new Error('Cloud saves are disabled in settings.');
    }

    if (!activePlayingRom) {
      throw new Error('This session is not linked to a saved ROM yet.');
    }

    const resolvedScreenshotBlob = screenshotBlob === undefined
      ? await captureCurrentGameThumbnailBlob()
      : screenshotBlob;

    const payloadKind = currentEmulator === 'dos' ? 'dos-changes' : 'console-state';
    const stateBlob = currentEmulator === 'dos'
      ? await captureDosStateBlob()
      : await captureConsoleStateBlob();

    if (!stateBlob || stateBlob.size === 0) {
      throw new Error(
        currentEmulator === 'dos'
          ? 'No DOS filesystem changes were detected yet to save.'
          : 'The current save state could not be captured.'
      );
    }

    const saved = await saveState({
      rom: activePlayingRom,
      type,
      payloadKind,
      stateBlob,
      screenshotBlob: resolvedScreenshotBlob ?? null,
      notes: null,
    });

    if (resolvedScreenshotBlob) {
      try {
        await uploadRomThumbnail(activePlayingRom, resolvedScreenshotBlob);
      } catch (error) {
        console.error('Failed to update ROM thumbnail from save screenshot:', error);
      }
    }

    if (saveDialogOpen) {
      await refreshSaveDialog(activePlayingRom.id);
    }

    return saved;
  };

  const triggerSessionSave = async (type: EmulatorSaveType, screenshotBlob?: Blob | null) => {
    setIsSavingState(true);
    try {
      const saved = await createSessionSave(type, screenshotBlob);
      if (type === 'auto') {
        setAutoSaveStatus({
          tone: 'success',
          message: saved.status === 'pending' ? 'Autosave queued offline. It will sync when you reconnect.' : 'Autosave completed.',
        });
      } else {
        toast({
          title: saved.status === 'pending' ? 'Save queued' : 'Save state created',
          description:
            saved.status === 'pending'
              ? 'Your save was stored locally and will sync when you reconnect.'
              : 'The current session was saved to your private cloud library.',
        });
      }
      return saved;
    } catch (error: any) {
      if (type === 'auto') {
        setAutoSaveStatus({
          tone: 'error',
          message: error?.message ?? 'Autosave failed.',
        });
      } else {
        toast({
          title: 'Save state failed',
          description: error?.message ?? 'The current session could not be saved.',
          variant: 'destructive',
        });
      }
      throw error;
    } finally {
      setIsSavingState(false);
    }
  };

  const handleLoadSave = async (save: CloudSaveListItem) => {
    if (!activePlayingRom) {
      return;
    }

    try {
      setLoadingSaveId(save.id);
      const stateBlob = await loadStateBlob(save);

      if (currentEmulator === 'console') {
        await applyConsoleStateBlob(stateBlob);
      } else {
        const system = SYSTEMS.find((item) => item.id === activePlayingRom.system_id);
        if (!system) {
          throw new Error('The active ROM system could not be resolved.');
        }

        const { data, error } = await supabase.storage.from(ROM_BUCKET).download(activePlayingRom.storage_path);
        if (error) {
          throw error;
        }

        const objectUrl = URL.createObjectURL(data);
        const launched = await launchObjectUrl(
          objectUrl,
          system,
          activePlayingRom.title,
          activePlayingRom.storage_path,
          activePlayingRom.original_file_name,
          stateBlob
        );

        if (!launched) {
          throw new Error('The DOS save could not be restored.');
        }
      }

      setSaveDialogOpen(false);
      toast({
        title: 'Save state loaded',
        description: 'Your game resumed from the selected save.',
      });
    } catch (error: any) {
      console.error('Failed to load save state:', error);
      toast({
        title: 'Load failed',
        description: error?.message ?? 'The selected save could not be loaded.',
        variant: 'destructive',
      });
    } finally {
      setLoadingSaveId(null);
    }
  };

  const handleDeleteSave = async (save: CloudSaveListItem) => {
    try {
      setDeletingSaveId(save.id);
      await deleteState(save);
      if (activePlayingRom) {
        await refreshSaveDialog(activePlayingRom.id);
      }
      toast({
        title: 'Save deleted',
        description: 'The save state and its cloud assets were removed.',
      });
    } catch (error: any) {
      console.error('Failed to delete save state:', error);
      toast({
        title: 'Delete failed',
        description: error?.message ?? 'The save could not be removed.',
        variant: 'destructive',
      });
    } finally {
      setDeletingSaveId(null);
    }
  };

  const incrementRomUsage = async (rom: RomLibraryItem) => {

    if (!user) {
      return rom;
    }

    const nextPlayedAt = new Date().toISOString();
    const updatedRom: RomLibraryItem = {
      ...rom,
      play_count: rom.play_count + 1,
      last_played_at: nextPlayedAt,
      updated_at: nextPlayedAt,
    };

    mergeRomIntoLibrary(updatedRom);

    try {
      const { error } = await supabaseAny
        .from('emulator_roms')
        .update({
          play_count: updatedRom.play_count,
          last_played_at: nextPlayedAt,
          updated_at: nextPlayedAt,
        })
        .eq('id', rom.id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Failed to update ROM usage:', error);
    }

    return updatedRom;
  };

  const uploadRomToLibrary = async (file: File, system: EmulatorSystem) => {
    if (!user) {
      return null;
    }

    const safeFileName = sanitizeStorageName(file.name);
    const storagePath = `${user.id}/${system.id}/${safeFileName}`;
    const title = stripExtension(file.name);
    const now = new Date().toISOString();

    const { error: uploadError } = await supabase.storage
      .from(ROM_BUCKET)
      .upload(storagePath, file, {
        upsert: true,
        cacheControl: '3600',
        contentType: file.type || undefined,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data, error } = await supabaseAny
      .from('emulator_roms')
      .upsert(
        {
          user_id: user.id,
          system_id: system.id,
          title,
          original_file_name: file.name,
          storage_path: storagePath,
          file_size: file.size,
          mime_type: file.type || null,
          updated_at: now,
        },
        {
          onConflict: 'user_id,system_id,original_file_name',
        }
      )
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    const rom = data as RomLibraryItem;
    mergeRomIntoLibrary(rom);
    if (rom.thumbnail_path) {
      await setThumbnailUrlForRom(rom);
    }
    return rom;
  };

  const stopEmulation = async () => {
    try {
      if (document.fullscreenElement === runningViewportRef.current) {
        await document.exitFullscreen();
      }

      const screenshotBlob = await captureCurrentGameThumbnailBlob();

      if (cloudSavesEnabled && activePlayingRom) {
        autoSaveInFlightRef.current = true;
        try {
          await triggerSessionSave('auto', screenshotBlob);
        } catch {
          // autosave failures are surfaced through status UI without blocking shutdown
        } finally {
          autoSaveInFlightRef.current = false;
        }
      }

      if (activePlayingRom && screenshotBlob) {
        try {
          await uploadRomThumbnail(activePlayingRom, screenshotBlob);
        } catch (error) {
          console.error('Failed to persist stop thumbnail:', error);
        }
      } else {
        await forceCaptureActiveRomThumbnail();
      }

      if (currentEmulator === 'dos') {
        await stopDosSession();
      } else if (currentEmulator === 'console') {
        setConsoleSession(null);
      }

      clearScheduledThumbnailCapture();
      currentPlayingRomIdRef.current = null;
      pendingConsoleAutoResumeRef.current = null;
      releaseActiveObjectUrl();
      setCurrentGameTitle('');
      setActivePlayingRomId(null);
      logCustomEvent('stop', { emulator: currentEmulator, path: currentPath });
      setIsRunning(false);
      setCurrentEmulator('none');
      setMainPanelTab('game');
    } catch (error) {
      console.error('Failed to stop emulation:', error);
      logCustomEvent('error', { emulator: currentEmulator, message: (error as any)?.message ?? 'Unknown' });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const loadFromFile = async () => {
    if (!selectedFile) {
      toast({ title: 'Error', description: 'Please select a ROM or game file first', variant: 'destructive' });
      return;
    }

    const matchingSystem = resolveSystemForFile(selectedFile.name, selectedSystem);

    if (!matchingSystem) {
      toast({ title: 'Error', description: 'Unsupported file format', variant: 'destructive' });
      return;
    }

    try {
      let savedRom: RomLibraryItem | null = null;

      if (user) {
        setIsPersistingRom(true);
        savedRom = await uploadRomToLibrary(selectedFile, matchingSystem);
      }

      const restoreBlob = savedRom ? await prepareLaunchRestore(savedRom, matchingSystem) : null;
      const objectUrl = URL.createObjectURL(selectedFile);
      const sourceLabel = savedRom?.storage_path ?? selectedFile.name;
      const launched = await launchObjectUrl(
        objectUrl,
        matchingSystem,
        stripExtension(selectedFile.name),
        sourceLabel,
        selectedFile.name,
        restoreBlob
      );

      if (launched && savedRom) {
        currentPlayingRomIdRef.current = savedRom.id;
        setActivePlayingRomId(savedRom.id);
        const updatedRom = await incrementRomUsage(savedRom);
        scheduleRomThumbnailCapture(updatedRom);
      } else {

        currentPlayingRomIdRef.current = null;
        pendingConsoleAutoResumeRef.current = null;
        setActivePlayingRomId(null);
      }
    } catch (error: any) {
      console.error('Failed to upload and load file:', error);
      toast({
        title: 'Upload failed',
        description: error?.message ?? 'Could not save this ROM to your library.',
        variant: 'destructive',
      });
    } finally {
      setIsPersistingRom(false);
    }
  };

  const playStoredRom = async (rom: RomLibraryItem) => {
    const system = SYSTEMS.find((item) => item.id === rom.system_id);
    if (!system) {
      toast({
        title: 'System unavailable',
        description: 'This ROM references an unsupported system.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setActiveRomId(rom.id);
      const restoreBlob = await prepareLaunchRestore(rom, system);
      const { data, error } = await supabase.storage.from(ROM_BUCKET).download(rom.storage_path);
      if (error) {
        throw error;
      }

      const objectUrl = URL.createObjectURL(data);
      const launched = await launchObjectUrl(
        objectUrl,
        system,
        rom.title,
        rom.storage_path,
        rom.original_file_name,
        restoreBlob
      );
      if (launched) {
        const updatedRom = await incrementRomUsage(rom);
        scheduleRomThumbnailCapture(updatedRom);
      }
    } catch (error: any) {
      console.error('Failed to play ROM from library:', error);
      toast({
        title: 'Could not load ROM',
        description: error?.message ?? 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setActiveRomId(null);
    }
  };

  const deleteStoredRom = async (rom: RomLibraryItem) => {
    if (!user) {
      return;
    }

    try {
      setDeletingRomId(rom.id);

      const { error } = await supabaseAny
        .from('emulator_roms')
        .delete()
        .eq('id', rom.id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      const pathsToRemove = [rom.storage_path, rom.thumbnail_path].filter(Boolean) as string[];
      if (pathsToRemove.length > 0) {
        const { error: storageError } = await supabase.storage.from(ROM_BUCKET).remove(pathsToRemove);
        if (storageError) {
          console.error('Failed to delete ROM assets:', storageError);
        }
      }

      removeRomFromLibrary(rom.id);
      toast({
        title: 'Game removed',
        description: `${rom.title} was removed from your library.`,
      });
    } catch (error: any) {
      console.error('Failed to delete ROM from library:', error);
      toast({
        title: 'Could not remove game',
        description: error?.message ?? 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeletingRomId(null);
    }
  };

  const handleLaunchDemo = async () => {
    clearScheduledThumbnailCapture();
    currentPlayingRomIdRef.current = null;
    pendingConsoleAutoResumeRef.current = null;
    setActivePlayingRomId(null);

    if (!selectedSystemDemo) {

      return;
    }

    if (selectedSystem.runtime === 'dos') {
      await loadDosGame(selectedSystemDemo.url, selectedSystemDemo.name, selectedSystemDemo.name);
      return;
    }

    await loadConsoleGame(
      selectedSystemDemo.url,
      selectedSystem,
      selectedSystemDemo.name,
      selectedSystemDemo.name,
      selectedSystemDemo.url.split('/').pop() ?? selectedSystemDemo.name
    );
  };

  const selectedSystemReady = selectedSystem.runtime === 'dos' ? isJsDosLoaded : isEmulatorJSLoaded;

  useEffect(() => {
    const syncConnectedGamepads = () => {
      if (typeof navigator.getGamepads !== 'function') {
        setConnectedGamepads([]);
        return;
      }

      const nextGamepads = Array.from(navigator.getGamepads())
        .filter((gamepad): gamepad is Gamepad => Boolean(gamepad))
        .map((gamepad) => ({
          id: gamepad.id,
          index: gamepad.index,
          mapping: gamepad.mapping || 'n/a',
        }));

      setConnectedGamepads(nextGamepads);
    };

    syncConnectedGamepads();
    const intervalId = window.setInterval(syncConnectedGamepads, 250);
    window.addEventListener('gamepadconnected', syncConnectedGamepads);
    window.addEventListener('gamepaddisconnected', syncConnectedGamepads);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('gamepadconnected', syncConnectedGamepads);
      window.removeEventListener('gamepaddisconnected', syncConnectedGamepads);
    };
  }, []);

  useEffect(() => {
    if (currentEmulator !== 'console' || !consoleSession) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      focusConsoleInput();
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [consoleSession, currentEmulator]);

  return (
    <div className={cn('h-full flex flex-col', shellClass)}>
      <div className={cn('border-b backdrop-blur-sm px-4 py-3', chromeClass)}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
              {isRunning ? (
                <SystemIconVisual
                  system={activeRunningSystem}
                  className="h-7 w-7 object-contain"
                  fallbackClassName="text-2xl leading-none"
                />
              ) : (
                <img
                  src={getAssetPath('/os/assets/icons/joystick.svg')}
                  alt="Retro Play"
                  className="h-6 w-6 object-contain"
                  draggable={false}
                />
              )}

              <Zap className="absolute -right-1 -top-1 h-3.5 w-3.5 text-yellow-500" />
            </div>
            <div>
              <h1 className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-xl font-bold text-transparent">
                {headerTitle}
              </h1>
              <p className={cn('text-xs', mutedTextClass)}>{headerSubtitle}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsGamepadTesterOpen(true)}
              className="flex items-center gap-2"
            >
              <Gamepad2 className="h-4 w-4" />
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                {connectedGamepads.length}
              </Badge>
            </Button>

            {isRunning && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void triggerSessionSave('manual')}
                  disabled={!activePlayingRom || isSavingState || !cloudSavesEnabled || !user}
                  className="flex items-center gap-2"
                >
                  {isSavingState ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Save State
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void openSaveDialogForCurrentRom()}
                  disabled={!activePlayingRom || !cloudSavesEnabled || !user}
                  className="flex items-center gap-2"
                >
                  <HardDriveDownload className="h-4 w-4" />
                  Load State
                </Button>
                <Button variant="outline" size="sm" onClick={toggleFullscreen} className="flex items-center gap-2">
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  {isFullscreen ? 'Exit' : 'Fullscreen'}
                </Button>
                <Button variant="destructive" size="sm" onClick={stopEmulation} className="flex items-center gap-2">
                  <Square className="h-4 w-4" />
                  Stop
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <>
          <div
            className={`flex min-h-0 overflow-hidden transition-[max-width,opacity,transform] duration-500 ease-in-out ${
              isRunning
                ? 'pointer-events-none max-w-0 -translate-x-8 opacity-0'
                : 'max-w-[520px] translate-x-0 opacity-100'
            }`}
          >
            <div className={cn('flex h-full min-h-0 w-24 shrink-0 flex-col border-r p-3', railClass)}>
              <div className="flex-1 space-y-3 overflow-y-auto pb-2 pr-1">
                {SYSTEMS.map((system) => {
                  const isSelected = system.id === selectedSystem.id;
                  const isReady = system.runtime === 'dos' ? isJsDosLoaded : isEmulatorJSLoaded;

                  return (
                    <button
                      key={system.id}
                      type="button"
                      title={system.name}
                      onClick={() => setSelectedSystemId(system.id)}
                      className={cn(
                        'group relative flex w-full items-center justify-center rounded-2xl border p-2 transition-all',
                        isSelected
                          ? isDark
                            ? 'scale-[1.04] border-primary/25 bg-primary/10 text-white/90 shadow-sm'
                            : 'scale-[1.04] border-primary/25 bg-primary/10 text-slate-950 shadow-sm'
                          : isDark
                            ? 'border-white/10 bg-black/20 text-white/90 hover:bg-black/30'
                            : 'border-slate-200/80 bg-white/80 text-slate-700 hover:bg-white'
                      )}
                    >
                      <span
                        className={cn(
                          'absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full transition-opacity',
                          isSelected ? 'bg-primary opacity-100' : isDark ? 'bg-white/10 opacity-0 group-hover:opacity-100' : 'bg-slate-300 opacity-0 group-hover:opacity-100'
                        )}
                      />
                      <div className="flex flex-col items-center gap-1.5">
                        <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl', isDark ? 'bg-white/10' : 'bg-slate-100')}>
                          <SystemIconVisual
                            system={system}
                            className="h-8 w-8 object-contain"
                            fallbackClassName="text-2xl leading-none"
                          />
                        </div>
                        <span className={cn('text-[10px] font-medium', isSelected ? (isDark ? 'text-white/90' : 'text-slate-950') : mutedTextClass)}>
                          {system.shortName}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={cn('w-[420px] shrink-0 overflow-y-auto border-r p-5', sidebarClass)}>
              <div className="space-y-5">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                        <SystemIconVisual
                          system={selectedSystem}
                          className="h-10 w-10 object-contain"
                          fallbackClassName="text-3xl leading-none"
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-semibold">{selectedSystem.name}</h2>
                          {selectedSystem.popular}
                        </div>
                        <p className={cn('text-sm', mutedTextClass)}>{selectedSystem.description}</p>
                      </div>
                    </div>
                    <Badge variant={selectedSystemReady ? 'default' : 'secondary'}>
                      {selectedSystemReady ? 'Ready' : 'Loading'}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{selectedSystem.shortName}</Badge>
                    <Badge variant="outline">{selectedSystem.year}</Badge>
                    <Badge variant="outline" className="capitalize">{selectedSystem.category}</Badge>
                    <Badge variant="outline">{selectedSystem.runtime === 'dos' ? 'DOS Runtime' : 'Console Runtime'}</Badge>
                  </div>
                </div>

                <Card className={cardClass}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Cpu className="h-4 w-4" />
                      System settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className={cn('rounded-xl border p-3', insetClass)}>
                        <div className={cn('text-xs uppercase tracking-wide', mutedTextClass)}>Emulator core</div>
                        <div className="mt-1 font-medium">{selectedSystem.core}</div>
                      </div>
                      <div className={cn('rounded-xl border p-3', insetClass)}>
                        <div className={cn('text-xs uppercase tracking-wide', mutedTextClass)}>Formats</div>
                        <div className="mt-1 font-medium">{selectedSystem.extensions.join(', ')}</div>
                      </div>
                    </div>

                    <div className={cn('rounded-xl border p-3 text-sm', insetClass)}>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Monitor className="h-4 w-4" />
                        Launch details
                      </div>
                      <p className={cn('mt-2 text-sm leading-relaxed', mutedTextClass)}>
                        Select a ROM for {selectedSystem.shortName}. If you upload a compressed archive, it will launch with the system you selected when supported.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className={cn('border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background shadow-sm', !isDark && 'bg-gradient-to-br from-primary/5 via-white to-slate-50')}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <HardDriveDownload className="h-4 w-4" />
                      ROM upload
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className={cn('rounded-2xl border border-dashed p-4', isDark ? 'border-white/30 bg-white/6' : 'border-slate-300 bg-white/70')}>
                      <div className="flex items-start gap-3">
                        <div className="mt-1 rounded-xl bg-primary/10 p-2">
                          <Cloud className="h-4 w-4 text-primary" />
                        </div>
                        <div className="space-y-2">
                          <p className="font-medium">{selectedSystem.name}</p>
                          <p className={cn('text-sm', mutedTextClass)}>
                            {user
                              ? `Supported formats: ${selectedSystem.extensions.join(', ')}. Your ROM is saved privately to your library.`
                              : `Supported formats: ${selectedSystem.extensions.join(', ')}. Sign in to sync uploads to your library.`}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rom-upload">ROM / game file</Label>
                      <Input
                        id="rom-upload"
                        ref={fileInputRef}
                        type="file"
                        accept={ALL_SUPPORTED_EXTENSIONS}
                        onChange={handleFileSelect}
                      />
                    </div>

                    {selectedFile && (
                      <div className={cn('rounded-xl border p-3 text-sm', insetClass)}>
                        <div className="font-medium">{selectedFile.name}</div>
                        <div className={cn('mt-1', mutedTextClass)}>Size: {formatBytes(selectedFile.size)}</div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button className="flex-1" onClick={loadFromFile} disabled={!selectedFile || isPersistingRom}>
                        {isPersistingRom ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="mr-2 h-4 w-4" />
                        )}
                        {user ? 'Play' : 'Play now'}
                      </Button>
                      <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Browse
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {selectedSystemDemo && (
                  <Card className={cardClass}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Zap className="h-4 w-4" />
                        Quick demo
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="font-medium">{selectedSystemDemo.name}</div>
                        <p className={cn('mt-1 text-sm', mutedTextClass)}>{selectedSystemDemo.description}</p>
                      </div>
                      <Button variant="outline" className="w-full" onClick={handleLaunchDemo}>
                        <Play className="mr-2 h-4 w-4" />
                        Launch demo
                      </Button>
                    </CardContent>
                  </Card>
                )}

                <Card className={cardClass}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Analytics & capture</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium">Share usage data</Label>
                        <p className={cn('mt-1 text-xs', mutedTextClass)}>Optional telemetry for emulator sessions.</p>
                      </div>
                      <Switch
                        checked={Boolean(appAnalytics.analyticsEnabled)}
                        onCheckedChange={(checked) => updateAppAnalytics('analyticsEnabled', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium">Capture screenshots</Label>
                        <p className={cn('mt-1 text-xs', mutedTextClass)}>Experimental session captures.</p>
                      </div>
                      <Switch
                        checked={Boolean(appAnalytics.screenshotsEnabled)}
                        onCheckedChange={(checked) => updateAppAnalytics('screenshotsEnabled', checked)}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => captureScreenshot()}
                        disabled={!analyticsEnabled || !appAnalytics.screenshotsEnabled}
                      >
                        Capture screenshot
                      </Button>
                      <Badge variant="outline">Global: {globalAnalytics.enabled ? 'On' : 'Off'}</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className={cardClass}>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Cloud className="h-4 w-4" />
                      Cloud saves
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium">Enable cloud saves</Label>
                        <p className={cn('mt-1 text-xs', mutedTextClass)}>
                          Save states stay private in Supabase storage and can sync after offline sessions.
                        </p>
                      </div>
                      <Switch
                        checked={cloudSavesEnabled}
                        onCheckedChange={(checked) => updateAppAnalytics('cloudSavesEnabled', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label className="text-sm font-medium">Auto-resume latest autosave</Label>
                        <p className={cn('mt-1 text-xs', mutedTextClass)}>
                          Automatically restore the newest autosave when you relaunch a library game.
                        </p>
                      </div>
                      <Switch
                        checked={autoResumeEnabled}
                        onCheckedChange={(checked) => updateAppAnalytics('autoResumeEnabled', checked)}
                        disabled={!cloudSavesEnabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Autosave interval</Label>
                      <Select
                        value={String(autoSaveIntervalMinutes)}
                        onValueChange={(value) => updateAppAnalytics('autoSaveIntervalMinutes', Number(value))}
                        disabled={!cloudSavesEnabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select interval" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Only when stopping a session</SelectItem>
                          <SelectItem value="2">Every 2 minutes</SelectItem>
                          <SelectItem value="5">Every 5 minutes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className={cn('rounded-xl border p-3 text-xs', isDark ? 'border-white/10 bg-black/20 text-white/60' : 'border-slate-200/80 bg-white/80 text-slate-600')}>
                      Retention is fixed at 10 save states per game. Older autosaves are pruned before manual saves.
                    </div>

                    {!user ? (
                      <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                        Sign in to save progress to the cloud and sync queued offline states.
                      </div>
                    ) : isSyncingPendingSaves ? (
                      <div className={cn('flex items-center gap-2 rounded-xl border p-3 text-xs', isDark ? 'border-white/10 bg-black/20 text-white/60' : 'border-slate-200/80 bg-white/80 text-slate-600')}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Syncing queued save states…
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          <div className={cn('flex-1 min-h-0 overflow-hidden', mainPanelClass)}>
            <Tabs value={mainPanelTab} onValueChange={(value) => setMainPanelTab(value as MainPanelTab)} className="flex h-full flex-col">
              <div className={cn('border-b px-6 py-4 backdrop-blur-sm', chromeClass)}>
                <div className="flex items-center justify-between gap-4">
                  <TabsList className={tabListClass}>
                    <TabsTrigger value="game" className={tabTriggerClass}>
                      <Monitor className="h-4 w-4" />
                      Game
                    </TabsTrigger>
                    <TabsTrigger value="library" className={tabTriggerClass}>
                      <Library className="h-4 w-4" />
                      Library
                    </TabsTrigger>
                  </TabsList>

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {isRunning && cloudSavesEnabled && activePlayingRom ? (
                      <div
                        className={cn(
                          'flex items-center gap-2 rounded-full border px-3 py-1 text-xs',
                          autoSaveStatus.tone === 'error'
                            ? 'border-rose-400/30 bg-rose-500/10 text-rose-100 dark:text-rose-100'
                            : autoSaveStatus.tone === 'success'
                              ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-100'
                              : isDark
                                ? 'border-white/10 bg-white/5 text-white/70'
                                : 'border-slate-200/80 bg-white/80 text-slate-700'
                        )}
                      >
                        <Cloud className="h-3.5 w-3.5" />
                        <span>{autoSaveStatus.message || 'Autosave ready'}</span>
                        {autoSaveStatus.tone === 'error' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={cn('h-6 px-2 text-[11px]', isDark ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100')}
                            onClick={() => void retryPendingSaves()}
                          >
                            Retry
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    <Badge className={badgeClass}>{selectedSystem.shortName}</Badge>
                    <Badge className={badgeClass}>{selectedSystemLibrary.length} saved</Badge>
                  </div>
                </div>
              </div>

              <TabsContent
                forceMount
                value="game"
                className={`relative mt-0 flex-1 overflow-hidden ${mainPanelTab === 'game' ? 'block' : 'hidden'}`}
              >
                {!isRunning ? (
                  <div className={`relative h-full w-full bg-gradient-to-br ${selectedSystem.heroGradient}`}>
                    {selectedSystem.heroBackgroundAsset && (
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-screen"
                        style={{ backgroundImage: `url(${getAssetPath(selectedSystem.heroBackgroundAsset)})` }}
                      />
                    )}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.16),transparent_28%),radial-gradient(circle_at_80%_25%,rgba(255,255,255,0.1),transparent_24%),radial-gradient(circle_at_50%_80%,rgba(255,255,255,0.08),transparent_30%)]" />

                    <div className="absolute inset-0 bg-gradient-to-r from-black/35 via-transparent to-black/45" />
                    <div className="absolute -right-16 top-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute bottom-10 left-10 h-48 w-48 rounded-full bg-white/5 blur-2xl" />

                    <div className="relative flex h-full flex-col justify-between overflow-y-auto px-8 py-8 pb-16 lg:px-12 lg:py-10 lg:pb-20">
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <Badge className={cn('mb-4', badgeClass)}>{selectedSystem.shortName} • {selectedSystem.year}</Badge>
                          <h2 className={cn('max-w-2xl text-4xl font-bold leading-tight lg:text-5xl', !isDark && 'text-white')}>
                            {selectedSystem.headline}
                          </h2>
                          <p className={cn('mt-4 text-sm font-medium uppercase tracking-[0.24em]', isDark ? 'text-white/70' : 'text-white/80')}>
                            full nostalgia mode
                          </p>
                        </div>
                      </div>

                      <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
                        <div className="space-y-5">
                          <p className={cn('max-w-xl text-sm leading-relaxed', isDark ? 'text-white/80' : 'text-white/90')}>
                            {selectedSystem.nostalgia}
                          </p>

                          <div className={cn('max-w-xl rounded-[1.75rem] border p-5 backdrop-blur-md', heroGlassClass)}>
                            <div className="mb-4 flex items-start justify-between gap-4">
                              <div>
                                <div className={cn('flex items-center gap-2 text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                                  <Zap className="h-4 w-4 text-yellow-300" />
                                  Quick access
                                </div>
                                <p className={cn('mt-1 text-sm', isDark ? 'text-white/65' : 'text-slate-700')}>
                                  Your most-played {selectedSystem.shortName} ROMs, ready for one-click replay.
                                </p>
                              </div>
                              <Badge className={badgeClass}>
                                {quickAccessRoms.length}/{QUICK_ACCESS_LIMIT}
                              </Badge>
                            </div>

                            {!user ? (
                              <div className={cn('rounded-2xl border p-4 text-sm', softInsetClass)}>
                                Sign in and uploaded ROMs will appear here automatically.
                              </div>
                            ) : quickAccessRoms.length === 0 ? (
                              <div className={cn('rounded-2xl border p-4 text-sm', softInsetClass)}>
                                Upload a few {selectedSystem.shortName} ROMs and the ones you play most will live here.
                              </div>
                            ) : (
                              <div className="grid gap-3 sm:grid-cols-2">
                                {quickAccessRoms.map((rom) => {
                                  const thumbnailUrl = thumbnailUrls[rom.id];

                                  return (
                                    <button
                                      key={rom.id}
                                      type="button"
                                      onClick={() => playStoredRom(rom)}
                                      disabled={activeRomId === rom.id}
                                      className={quickAccessCardClass}
                                    >
                                      <RomThumbnailVisual
                                        rom={rom}
                                        system={selectedSystem}
                                        thumbnailUrl={thumbnailUrl}
                                        className="h-28 w-full rounded-none border-0"
                                      />
                                      <div className="p-4">
                                        <div className={cn('truncate text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                                          {rom.title}
                                        </div>
                                        <div className={cn('mt-1 flex items-center justify-between text-xs', mutedTextClass)}>
                                          <span>{rom.play_count} plays</span>
                                          <span>{formatLibraryDate(rom.last_played_at || rom.created_at)}</span>
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="relative flex items-center justify-center">
                          <div className={cn('absolute inset-0 m-auto h-72 w-72 rounded-full blur-2xl', isDark ? 'bg-white/10' : 'bg-white/55')} />
                          <div className={cn('relative flex aspect-square w-full max-w-[360px] items-center justify-center rounded-[2rem] border p-8 shadow-2xl backdrop-blur-sm', heroGlassClass)}>
                            <SystemIconVisual
                              system={selectedSystem}
                              className="h-full w-full object-contain drop-shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
                              fallbackClassName="text-[11rem] leading-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div ref={runningViewportRef} className="relative h-full bg-black">
                    <div
                      ref={dosRef}
                      className={`h-full w-full ${currentEmulator === 'dos' ? 'block' : 'hidden'}`}
                      style={{ minHeight: '400px', cursor: isRunning && currentEmulator === 'dos' ? 'none' : 'default' }}
                    />

                    <div className={`h-full w-full ${currentEmulator === 'console' ? 'block' : 'hidden'}`} style={{ minHeight: '400px' }}>
                      {consoleSession && (
                        <iframe
                          key={`${consoleSession.system.id}-${consoleSession.url}`}
                          ref={consoleFrameRef}
                          title={`${consoleSession.system.name} emulator`}
                          srcDoc={consoleHtml}
                          className="h-full w-full border-0"
                          tabIndex={0}
                          sandbox="allow-scripts allow-same-origin"
                          referrerPolicy="no-referrer"
                          allow="gamepad; fullscreen"
                          allowFullScreen
                          onLoad={focusConsoleInput}
                          onPointerDown={focusConsoleInput}
                        />
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent
                forceMount
                value="library"
                className={`mt-0 flex-1 overflow-hidden ${libraryPanelClass} ${mainPanelTab === 'library' ? 'block' : 'hidden'}`}
              >
                <div className="flex h-full flex-col p-6 lg:p-8">
                  <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className={cn('flex items-center gap-2 text-sm font-semibold', isDark ? 'text-white/90' : 'text-slate-800')}>
                        <Library className="h-4 w-4" />
                        {selectedSystem.name} library
                      </div>
                      <h3 className={cn('mt-2 text-3xl font-bold', isDark ? 'text-white' : 'text-slate-950')}>Your games for this system</h3>
                      <p className={cn('mt-2 max-w-2xl text-sm', isDark ? 'text-white/65' : 'text-slate-600')}>
                        Switch consoles to browse each system library. Launching any game automatically returns you to the Game tab.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={badgeClass}>{selectedSystem.shortName}</Badge>
                      <Badge className={badgeClass}>{selectedSystemLibrary.length} games</Badge>
                    </div>
                  </div>

                  {!user ? (
                    <div className={cn('flex flex-1 items-center justify-center rounded-[2rem] border p-8 text-center text-sm', softInsetClass)}>
                      Sign in to build a personal ROM library for each console and relaunch games from here.
                    </div>
                  ) : libraryLoading ? (
                    <div className={cn('flex flex-1 items-center justify-center rounded-[2rem] border p-8 text-sm', softInsetClass)}>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading your library...
                    </div>
                  ) : selectedSystemLibrary.length === 0 ? (
                    <div className={cn('flex flex-1 items-center justify-center rounded-[2rem] border border-dashed p-8 text-center text-sm', isDark ? 'border-white/15 bg-white/5 text-white/70' : 'border-slate-300 bg-white/70 text-slate-700')}>
                      Upload your first {selectedSystem.shortName} ROM and it will appear here as a playable library card.
                    </div>
                  ) : (
                    <ScrollArea className="flex-1 pr-2">
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {selectedSystemLibrary.map((rom) => {
                          const thumbnailUrl = thumbnailUrls[rom.id];
                          const isActive = activeRomId === rom.id;
                          const isDeleting = deletingRomId === rom.id;

                          return (
                            <div key={rom.id} className={cn('group relative overflow-hidden rounded-[1.75rem] border', libraryCardClass)}>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => void deleteStoredRom(rom)}
                                disabled={isActive || isDeleting}
                                className={dismissButtonClass}
                                aria-label={`Remove ${rom.title} from library`}
                              >
                                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                              </Button>

                              <button
                                type="button"
                                onClick={() => playStoredRom(rom)}
                                disabled={isActive || isDeleting}
                                className="block w-full text-left disabled:opacity-70"
                              >
                                <RomThumbnailVisual
                                  rom={rom}
                                  system={selectedSystem}
                                  thumbnailUrl={thumbnailUrl}
                                  className="h-44 w-full rounded-none border-0"
                                />
                                <div className="space-y-4 p-5">
                                  <div className="flex items-start justify-between gap-3 pr-8">
                                    <div className="min-w-0">
                                      <div className={cn('truncate text-base font-semibold', isDark ? 'text-white' : 'text-slate-950')}>
                                        {rom.title}
                                      </div>
                                      <div className={cn('mt-1 truncate text-sm', faintTextClass)}>{rom.original_file_name}</div>
                                    </div>
                                    <Badge className={cn('shrink-0', badgeClass)}>{selectedSystem.shortName}</Badge>
                                  </div>

                                  <div className={cn('flex flex-wrap gap-2 text-xs', mutedTextClass)}>
                                    <span className={cn('rounded-full border px-2.5 py-1', isDark ? 'border-white/10' : 'border-slate-200 bg-slate-50')}>
                                      {formatBytes(rom.file_size)}
                                    </span>
                                    <span className={cn('rounded-full border px-2.5 py-1', isDark ? 'border-white/10' : 'border-slate-200 bg-slate-50')}>
                                      {rom.play_count} plays
                                    </span>
                                    <span className={cn('rounded-full border px-2.5 py-1', isDark ? 'border-white/10' : 'border-slate-200 bg-slate-50')}>
                                      {formatLibraryDate(rom.last_played_at || rom.created_at)}
                                    </span>
                                  </div>

                                  <div className={cn('flex items-center justify-between text-sm', softTextClass)}>
                                    <span>{thumbnailUrl ? 'Captured thumbnail' : 'Thumbnail after first play'}</span>
                                    <span className={cn('inline-flex items-center gap-2 font-medium', isDark ? 'text-white' : 'text-slate-950')}>
                                      {isActive ? (
                                        <>
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                          Loading
                                        </>
                                      ) : (
                                        <>
                                          Play
                                          <Play className="h-4 w-4" />
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </>
      </div>

      <RetroPlayGamepadDialog
        open={isGamepadTesterOpen}
        onOpenChange={setIsGamepadTesterOpen}
        preferredGamepadId={selectedControllerId || null}
      />

      <EmulatorSaveDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        saves={saveDialogSaves}
        isLoading={saveDialogLoading}
        loadingSaveId={loadingSaveId}
        deletingSaveId={deletingSaveId}
        onLoad={handleLoadSave}
        onDelete={handleDeleteSave}
        onRetryPending={async () => {
          await retryPendingSaves();
          await refreshSaveDialog();
        }}
      />

    </div>
  );
};